import { sha256 as nobleSha256 } from '@noble/hashes/sha2'
import { bytesToHex } from '@noble/hashes/utils'
import { SdkError } from './errors'
import type { WorkspaceAuthProvider } from './workspaceSecretV2'
import { validateName, validatePath } from './pathUtils'
import { canonicalJsonStringify } from './workspaceSecretV2'
import type {
    WorkspaceTemplate,
    WorkspaceTemplateMeta,
    WorkspaceTemplateFile,
    WorkspaceTemplateDirectory,
} from './workspaceTemplate'
import { applyWorkspaceTemplate } from './workspaceTemplate'

const encoder = new TextEncoder()

export const COMPONENT_BUILD_STAGES = [
    'queued',
    'resolving_toolchain',
    'materializing_snapshot',
    'restoring_packages',
    'bundling',
    'componentizing',
    'writing_artifacts',
    'ready',
    'failed',
] as const

export const COMPONENT_TERMINAL_BUILD_STAGES = ['ready', 'failed'] as const
export const COMPONENT_RUN_STAGES = ['queued', 'starting', 'running', 'completed', 'failed', 'cancelled'] as const
export const COMPONENT_TERMINAL_RUN_STAGES = ['completed', 'failed', 'cancelled'] as const

export const COMPONENT_SERVER_ENGINES = ['wasmtime', 'node'] as const
export const COMPONENT_BUILD_TARGET_KINDS = ['single-file', 'job-root'] as const
export const COMPONENT_JOB_SOURCE_EXTENSIONS = ['.js', '.jsx', '.ts', '.tsx', '.mjs', '.mts', '.cjs', '.cts'] as const
export const COMPONENT_JOB_SCHEDULE_PRESETS = ['manual', 'every-5-min', 'hourly', 'daily', 'custom'] as const
export const COMPONENT_JOB_ENGINE_POLICIES = ['auto', 'prefer-wasmtime'] as const
export const COMPONENT_JOB_CONCURRENCY_POLICIES = ['skip-if-running', 'queue', 'replace'] as const
export const COMPONENT_JOB_STARTER_TEMPLATES = ['container-service', 'minimal', 'multi-file-service'] as const
export const COMPONENT_WEBHOOK_EVENTS = ['run.cancelled', 'run.completed', 'run.failed'] as const
export const DEFAULT_COMPONENT_JOB_TIMEZONE = 'UTC'

const SQLITE_IMPORT_PATTERNS = [
    /^sqlite$/,
    /^sqlite\//,
    /^@sqlite\.org\/sqlite-wasm$/,
    /sqlite3mt/,
    /sqlite-wasm/,
]

const MCP_IMPORT_PATTERNS = [
    /^mcp$/,
    /^mcp\//,
]

const LLM_IMPORT_PATTERNS = [
    /^agent$/,
    /^agent\//,
    /^llm$/,
    /^llm\//,
]

const SQLITE_SOURCE_PATTERNS = [
    /\bopenSqlite\s*\(/,
    /\.openSqlite\s*\(/,
]

const MCP_SOURCE_PATTERNS = [
    /\bcreateMcpClient\s*\(/,
    /\bmcp\.mount\s*\(/,
    /\bmcp\.call\s*\(/,
]

const LLM_SOURCE_PATTERNS = [
    /\bcreateAgentClient\s*\(/,
    /\bcreateLlmExtractionPipeline\s*\(/,
    /\bsecureblob_extract\.structured\b/,
]

export type ComponentBuildStage = typeof COMPONENT_BUILD_STAGES[number]
export type ComponentRunStage = typeof COMPONENT_RUN_STAGES[number]
export type ComponentServerEngine = typeof COMPONENT_SERVER_ENGINES[number]
export type ComponentBuildTargetKind = typeof COMPONENT_BUILD_TARGET_KINDS[number]
export type ComponentJobSourceExtension = typeof COMPONENT_JOB_SOURCE_EXTENSIONS[number]
export type ComponentJobSchedulePreset = typeof COMPONENT_JOB_SCHEDULE_PRESETS[number]
export type ComponentJobEnginePolicy = typeof COMPONENT_JOB_ENGINE_POLICIES[number]
export type ComponentJobConcurrencyPolicy = typeof COMPONENT_JOB_CONCURRENCY_POLICIES[number]
export type ComponentJobStarterTemplate = typeof COMPONENT_JOB_STARTER_TEMPLATES[number]
export type ComponentWebhookEvent = typeof COMPONENT_WEBHOOK_EVENTS[number]
export type ComponentRuntimeProfileValue = string | number | boolean | null
export type ComponentTriggerDispatchStatus = 'queued'
export type ComponentJobPathKind =
    | 'job_root'
    | 'source'
    | 'settings'
    | 'runs_dir'
    | 'run_dir'
    | 'run_metadata'
    | 'run_stdout'
    | 'run_stderr'
    | 'run_events'
    | 'run_http'
    | 'run_mcp'
    | 'run_status_cards'
    | 'run_outputs_dir'
    | 'run_output'
    | 'run_file'
    | 'builds_dir'
    | 'build_attempts_dir'
    | 'build_attempt_dir'
    | 'build_attempt_metadata'
    | 'build_attempt_build_log'
    | 'build_attempt_outputs_dir'
    | 'build_attempt_output'
    | 'build_attempt_file'
    | 'artifact_dir'
    | 'artifact_manifest'
    | 'artifact_component_wasm'
    | 'artifact_build_log'
    | 'artifact_file'

export interface ComponentJobSettings {
    enabled: boolean
    target: 'server'
    schedulePreset: ComponentJobSchedulePreset
    cron: string
    timezone: string
    payloadPreset: string
    enginePolicy: ComponentJobEnginePolicy
    concurrencyPolicy: ComponentJobConcurrencyPolicy
}

export interface NormalizeComponentJobSettingsOptions {
    fallbackTimezone?: string
}

export interface ComponentJobServerRunner {
    purpose: 'server-runner'
    label: string
    configBlobId: string
    connectionCredential: string
}

export interface ComponentJobConfig {
    v: '1'
    type: 'component-job-config'
    entry?: string
    schedule: ComponentJobSettings
    serverRunner?: ComponentJobServerRunner
}

export type NormalizeComponentJobConfigOptions = NormalizeComponentJobSettingsOptions

export interface CreateComponentJobStarterSourceOptions {
    template?: 'container-service'
}

/**
 * A single file entry in a scaffold definition.
 * `path` is relative to the job root (e.g. "lib/helpers.ts").
 * `content` is the file's text content.
 */
export interface ComponentJobScaffoldFile {
    path: string
    content: string
}

/**
 * A scaffold describes the full set of files and directories that a starter
 * preset should materialize inside a folder-first job root.
 *
 * - `files`: the files to write (path relative to job root, content as string).
 *   The first file is considered the primary entrypoint.
 * - `directories`: optional user-owned directories to create (e.g. "lib", "templates").
 *   These are relative to the job root and must NOT start with `.secureblob/`.
 * - `label`: human-readable name for the preset (e.g. "Container Service").
 * - `description`: short description shown in starter menus.
 */
export interface ComponentJobScaffold {
    template: ComponentJobStarterTemplate
    label: string
    description: string
    files: ComponentJobScaffoldFile[]
    directories: string[]
}

export interface CreateComponentJobScaffoldOptions {
    template?: ComponentJobStarterTemplate
}

export interface ComponentCapabilityRequirements {
    requiresSqlite: boolean
    requiresLlm: boolean
    requiresMcp: boolean
    requiresNetwork: boolean
    requiresAsyncHost: boolean
}

export interface DeriveComponentCapabilityRequirementsInput extends Partial<ComponentCapabilityRequirements> {
    imports?: Iterable<string>
    sourceText?: string | Iterable<string>
    usesFetch?: boolean
}

export interface ComponentArtifactToolchain {
    esbuild: string
    componentizeJs: string
    [key: string]: string
}

export interface ComponentArtifactIdentityInput {
    sourceSnapshot: string
    entrypoints: readonly string[]
    dependencySnapshot: string
    worldVersion: string
    toolchain: ComponentArtifactToolchain
    runtimeProfile: Readonly<Record<string, ComponentRuntimeProfileValue>>
}

export interface ComponentArtifactFileEntry {
    path: string
    sha256?: string
    size?: number
}

export interface ComponentBuildTarget {
    kind: ComponentBuildTargetKind
    declaredPath: string
}

export interface ComponentGuestSdkInfo {
    name: string
    version: string
    runtimePackage: string
    runtimeVersion: string
}

export interface ComponentHostCapabilities {
    configTransfer: boolean
    fetch: boolean
    streams: boolean
    sqlite: boolean
    stdout: boolean
    stderr: boolean
    events: boolean
}

export interface ComponentSdkSurfaces {
    workspaceFs: boolean
    mcp: boolean
    agent: boolean
    shimmedFetch: boolean
}

export interface ComponentRunnerEngineCompatibility {
    supported: boolean
    required: boolean
    asyncModel?: string
    reason?: string
    localStorePolicy?: 'memory' | 'indexeddb'
}

export interface ComponentRunnerCompatibility {
    wasmtime: ComponentRunnerEngineCompatibility
    node?: ComponentRunnerEngineCompatibility
}

export interface ComponentArtifactManifest {
    artifactId: string
    sourceSnapshot: string
    buildTarget: ComponentBuildTarget
    entrypoints: string[]
    createdAt: string
    worldVersion: string
    bindingFamily: string
    guestSdk: ComponentGuestSdkInfo
    toolchain: ComponentArtifactToolchain
    hostCapabilities: ComponentHostCapabilities
    sdkSurfaces: ComponentSdkSurfaces
    requiresSqlite: boolean
    requiresLlm: boolean
    requiresMcp: boolean
    requiresNetwork: boolean
    requiresAsyncHost: boolean
    recommendedServerEngine: ComponentServerEngine
    runnerCompatibility: ComponentRunnerCompatibility
    files: ComponentArtifactFileEntry[]
}

export interface CreateComponentArtifactManifestInput extends ComponentArtifactIdentityInput, DeriveComponentCapabilityRequirementsInput {
    createdAt: string
    files?: readonly ComponentArtifactFileEntry[]
    recommendedServerEngine?: ComponentServerEngine | 'auto'
    buildTarget?: ComponentBuildTarget
    bindingFamily?: string
    guestSdk?: ComponentGuestSdkInfo
    hostCapabilities?: ComponentHostCapabilities
    sdkSurfaces?: ComponentSdkSurfaces
    runnerCompatibility?: ComponentRunnerCompatibility
}

export interface ComponentRunnerBootstrap {
    apiBaseUrl: string
    workspaceBlobId?: string
    workspaceAlias?: string
    workspaceSecret?: string
    configConnectionToken?: string
    localStorePolicy?: 'memory' | 'indexeddb'
    secretEnv?: Record<string, string>
}

export interface ComponentJobCompanionPaths {
    settings: string
    runsDir: string
    buildsDir: string
}

export interface ComponentRunPaths {
    runDir: string
    metadata: string
    stdout: string
    stderr: string
    events: string
    http: string
    mcp: string
    statusCards: string
    outputsDir: string
}

export interface ComponentRunAgentSessionPaths {
    sessionsDir: string
    sessionDir: string
    transcript: string
}

export interface ComponentArtifactPaths {
    artifactDir: string
    componentWasm: string
    manifest: string
    buildLog: string
}

export interface ComponentBuildAttemptPaths {
    attemptsDir: string
    attemptDir: string
    metadata: string
    buildLog: string
    outputsDir: string
}

export interface ParsedComponentJobPath {
    path: string
    sourcePath: string
    kind: ComponentJobPathKind
    runId?: string
    buildId?: string
    artifactId?: string
    relativePath?: string
}

export type ComponentJobsFetch = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>

export interface ComponentJobsClientOptions {
    apiBaseUrl: string
    fetch?: ComponentJobsFetch
    headers?: HeadersInit
    /**
     * Optional workspace auth provider for component-job control-plane calls.
     *
     * Workspace access tokens are intentionally short lived. Long-running
     * component jobs should pass an auth provider instead of a fixed
     * Authorization header so polling, log download, and follow-up requests can
     * refresh credentials after the initial five-minute token expires.
     */
    authProvider?: WorkspaceAuthProvider
    authScope?: 'read' | 'write'
    signal?: AbortSignal
}

export interface ComponentBuildStatusRecord {
    buildId: string
    sourceSnapshot: string
    entrypoints: string[]
    workspaceBlobId: string
    stage: ComponentBuildStage
    artifactId?: string
    error?: string
    message: string
    startedAt: string
    stageStartedAt: string
    totalElapsedSeconds: number
    stageElapsedSeconds: number
    updatedAt: string
}

export interface ComponentRunStatusRecord {
    runId: string
    buildId?: string
    artifactId?: string
    workspaceBlobId: string
    sourcePath?: string
    engine?: ComponentServerEngine
    stage: ComponentRunStage
    error?: string
    message: string
    startedAt: string
    stageStartedAt: string
    totalElapsedSeconds: number
    stageElapsedSeconds: number
    updatedAt: string
}

export interface ComponentTriggerDispatchRecord {
    buildId: string
    artifactId?: string
    runId: string
    workspaceBlobId: string
    sourcePath: string
    status: ComponentTriggerDispatchStatus
}

export interface ComponentWebhookRegistrationRecord {
    webhookId: string
    workspaceBlobId: string
    sourcePath: string
    callbackUrl: string
    events: ComponentWebhookEvent[]
    hasRunnerBootstrap: boolean
    createdAt: string
    updatedAt: string
}

export type ComponentWebhookDeliveryStatus = 'pending' | 'delivered' | 'failed' | 'retry_scheduled' | 'dead'
export type ComponentWebhookDeliveryAttemptStatus = 'delivered' | 'failed' | 'cancelled'

export interface ComponentWebhookDeliveryAttempt {
    attemptNumber: number
    attemptedAt: string
    httpStatusCode: number | null
    status: ComponentWebhookDeliveryAttemptStatus
    error: string | null
}

export interface ComponentWebhookDeliveryRecord {
    deliveryId: string
    webhookId: string
    runId: string
    event: string
    attemptCount: number
    createdAt: string
    updatedAt: string
    lastAttemptAt: string | null
    nextRetryAt: string | null
    httpStatusCode: number | null
    status: ComponentWebhookDeliveryStatus
    lastError: string | null
    attempts: ComponentWebhookDeliveryAttempt[]
}

export interface ComponentWebhookWithDeliveriesRecord extends ComponentWebhookRegistrationRecord {
    deliveries: ComponentWebhookDeliveryRecord[]
}

export interface ComponentJobScheduleRecord {
    scheduleId: string
    artifactId: string
    workspaceBlobId: string
    sourcePath: string
    enabled: boolean
    schedulePreset: ComponentJobSchedulePreset
    cron: string
    timezone: string
    enginePolicy: ComponentJobEnginePolicy
    concurrencyPolicy: ComponentJobConcurrencyPolicy
    payloadPreset?: string
    jobRoot?: string
    successors?: ComponentJobSuccessor[]
    createdAt: string
    updatedAt: string
}

export interface ComponentJobSuccessor {
    sourcePath: string
}

export interface ActiveComponentJobsRecord {
    builds: ComponentBuildStatusRecord[]
    runs: ComponentRunStatusRecord[]
}

export interface WorkspaceComponentJobsRecord extends ActiveComponentJobsRecord {
    schedules: ComponentJobScheduleRecord[]
}

export interface SecureBlobAppIacStatusResult {
    configBlobId: string
    appManifestPath: string
    mailboxRoot: string
    healthy: boolean
    workspaces: SecureBlobAppIacWorkspaceStatus[]
    managedRegistrations: SecureBlobAppIacManagedRegistrationStatus[]
    warnings: string[]
    errors: string[]
}

export interface SecureBlobAppIacManagedRegistrationStatus {
    id: string
    enabled: boolean
    hasConfigConnectionToken: boolean
    workspaceRefs: string[]
    scanRoots: string[]
    manifestPaths: string[]
    lastReconciledAt?: string | null
    lastError?: string | null
}

export interface SecureBlobAppIacWorkspaceStatus {
    workspaceRef: string
    workspaceBlobId: string
    hasWorkspaceSecret: boolean
    hasWriterSeed: boolean
    configPermissionsAllowWrite: boolean
    healthy: boolean
    appManifest: {
        path: string
        exists: boolean
        type?: string | null
        appId?: string | null
        description?: string | null
        componentJobs: Array<{ id: string; jobRoot: string }>
        error?: string | null
    }
    componentJobs: SecureBlobAppIacComponentJobStatus[]
    mailbox: {
        root: string
        expected: boolean
        healthy: boolean
        existingBugSlots: string[]
        missingBugSlots: string[]
        existingFeatureSlots: string[]
        missingFeatureSlots: string[]
    }
    warnings: string[]
    errors: string[]
}

export interface SecureBlobAppIacComponentJobStatus {
    id: string
    jobRoot: string
    healthy: boolean
    jobConfigExists: boolean
    scheduleExists: boolean
    statusExists: boolean
    jobConfigPath: string
    schedulePath: string
    statusPath: string
    entrypoints: string[]
    enabled: boolean
    schedulePreset?: string | null
    cron?: string | null
    artifactId?: string | null
    lastBuildId?: string | null
    lastPromotedAt?: string | null
    sourceSnapshot?: string | null
    currentSourceSnapshot?: string | null
    sourceSnapshotMatches?: boolean | null
    artifactStorePresent: boolean
    artifactWorkspaceManifestExists: boolean
    artifactRehydratableFromWorkspace: boolean
    statusDocument?: {
        lastReconciledAt?: string | null
        buildId?: string | null
        buildStage?: string | null
        artifactId?: string | null
        sourceSnapshot?: string | null
        message?: string | null
        error?: string | null
        updatedAt?: string | null
        artifactRetention?: SecureBlobAppIacArtifactRetentionStatus | null
    } | null
    registeredSchedule?: {
        exists: boolean
        sourcePath?: string | null
        artifactId?: string | null
        enabled?: boolean | null
        schedulePreset?: string | null
        cron?: string | null
        updatedAt?: string | null
        successors?: string[] | null
    } | null
    recentBuilds: Array<{
        buildId: string
        stage: string
        artifactId?: string | null
        sourceSnapshot: string
        entrypoints: string[]
        message?: string | null
        error?: string | null
        updatedAt: string
    }>
    recentRuns: Array<{
        runId: string
        stage: string
        artifactId?: string | null
        engine?: string | null
        message?: string | null
        error?: string | null
        updatedAt: string
    }>
    statusCards?: SecureBlobAppIacStatusCard[]
    missingPaths: string[]
    warnings: string[]
    errors: string[]
}

export interface SecureBlobAppIacArtifactRetentionStatus {
    enabled: boolean
    dryRun: boolean
    keepPrevious: number
    retainNonCurrentDays: number
    maxDeletesPerPass: number
    scannedArtifactCount: number
    protectedArtifactCount: number
    retainedArtifactCount: number
    deleteCandidateCount: number
    deletedArtifactCount: number
    skippedArtifactCount: number
    protectedArtifactIds: string[]
    retainedArtifactIds: string[]
    candidateArtifactIds: string[]
    deletedArtifactIds: string[]
    warnings: string[]
    updatedAt: string
}

export type SecureBlobAppIacStatusCardTone = 'ok' | 'warn' | 'error' | 'info' | 'neutral'

export interface SecureBlobAppIacStatusCard {
    id: string
    title: string
    status: string
    detail?: string | null
    tone: SecureBlobAppIacStatusCardTone
    updatedAt?: string | null
    source?: string | null
}

export interface PrepareBuildOptions extends ComponentJobsClientOptions {
    sourceSnapshot: string
    entrypoints: readonly string[]
    workspaceBlobId: string
    runnerBootstrap: ComponentRunnerBootstrap
}

export interface GetBuildStatusOptions extends ComponentJobsClientOptions {
    buildId: string
}

export interface DownloadBuildFileOptions extends ComponentJobsClientOptions {
    buildId: string
    fileName: string
}

export interface MaterializeBuildOptions extends ComponentJobsClientOptions {
    buildId: string
    targetPath: string
    fileNames?: readonly string[]
}

export interface InspectArtifactOptions extends ComponentJobsClientOptions {
    artifactId: string
}

export interface DownloadArtifactFileOptions extends ComponentJobsClientOptions {
    artifactId: string
    fileName: string
}

export interface RunServerOptions extends ComponentJobsClientOptions {
    artifactId: string
    workspaceBlobId: string
    engine?: ComponentServerEngine | 'auto'
    runInput?: string | null
    sourcePath?: string
    runnerBootstrap?: ComponentRunnerBootstrap
    jobRoot?: string
}

/**
 * Typed lifecycle hooks invoked by `runServerWithHooks` at each stage transition.
 * All callbacks are optional; the SDK safely skips null hooks.
 * Hooks fire on both success and failure paths.
 */
export interface ComponentJobLifecycleHooks {
    /** Invoked before the build is prepared (receives source snapshot path). */
    onPreBuild?: (sourcePath: string) => void
    /** Invoked after the build artifact is materialized (receives artifact ID and manifest). */
    onPostMaterialize?: (artifactId: string, manifest: ComponentArtifactManifest) => void
    /** Invoked just before the run is submitted to the server (receives run ID). */
    onPreRun?: (runId: string) => void
    /** Invoked when the run reaches a terminal stage (receives run ID and final status). */
    onPostRun?: (runId: string, result: ComponentRunStatusRecord) => void
    /** Invoked on any error during the lifecycle (receives stage name and error). */
    onError?: (stage: string, error: Error) => void
}

export interface GetRunStatusOptions extends ComponentJobsClientOptions {
    runId: string
}

export interface CancelRunOptions extends ComponentJobsClientOptions {
    runId: string
}

export interface TriggerRunOptions extends ComponentJobsClientOptions {
    sourcePath: string
    workspaceBlobId: string
    engine?: ComponentServerEngine | 'auto'
    runInput?: string | null
    runnerBootstrap?: ComponentRunnerBootstrap
}

export interface CallHandlerOptions extends ComponentJobsClientOptions {
    sourcePath: string
    workspaceBlobId: string
    engine?: ComponentServerEngine | 'auto'
    inputJson?: string | null
    runnerBootstrap?: ComponentRunnerBootstrap
    timeoutSeconds?: number
}

export interface ComponentHandlerCallResult {
    statusCode: number
    durationMs: number
    runId?: string
    output?: unknown
    message?: string
    errorCode?: string
}

export interface DownloadRunLogOptions extends ComponentJobsClientOptions {
    runId: string
    logName: string
}

export interface TailRunLogOptions extends ComponentJobsClientOptions {
    runId: string
    logName: string
    fromLine?: number
}

export interface ScheduleOptions extends ComponentJobsClientOptions {
    sourcePath: string
    artifactId: string
    workspaceBlobId: string
    enabled: boolean
    schedulePreset: ComponentJobSchedulePreset
    cron?: string
    timezone?: string
    enginePolicy?: ComponentJobEnginePolicy
    concurrencyPolicy?: ComponentJobConcurrencyPolicy
    payloadPreset?: string | null
    runnerBootstrap?: ComponentRunnerBootstrap
    jobRoot?: string
}

export interface GetSchedulesOptions extends ComponentJobsClientOptions {
    workspaceBlobId?: string
}

export interface DisableScheduleOptions extends ComponentJobsClientOptions {
    sourcePath: string
    workspaceBlobId: string
}

export interface UpsertWebhookOptions extends ComponentJobsClientOptions {
    sourcePath: string
    workspaceBlobId: string
    callbackUrl: string
    secret: string
    events: readonly string[]
    runnerBootstrap: ComponentRunnerBootstrap
}

export interface GetWebhooksOptions extends ComponentJobsClientOptions {
    workspaceBlobId: string
}

export interface DeleteWebhookOptions extends ComponentJobsClientOptions {
    webhookId: string
}

export interface GetWebhookDeliveriesOptions extends ComponentJobsClientOptions {
    webhookId: string
}

export interface GetWorkspaceJobsOptions extends ComponentJobsClientOptions {
    workspaceBlobId: string
    sourcePath?: string
    limit?: number
}

export interface GetAppIacStatusOptions extends ComponentJobsClientOptions {
    configConnectionToken: string
    workspaceRefs: readonly string[]
    appManifestPath?: string
    mailboxRoot?: string
}

export function isTerminalComponentBuildStage(stage: ComponentBuildStage): boolean {
    return stage === 'ready' || stage === 'failed'
}

export function isTerminalComponentRunStage(stage: ComponentRunStage): boolean {
    return stage === 'completed' || stage === 'failed' || stage === 'cancelled'
}

export function isComponentJobEntrypointPath(path: string): boolean {
    try {
        validateComponentEntrypoint(path)
        return true
    } catch {
        return false
    }
}

export function normalizeComponentEntrypoints(entrypoints: readonly string[]): string[] {
    if (entrypoints.length === 0) {
        throw new SdkError('INVALID_PATH', 'Component builds require at least one entrypoint')
    }

    return [...new Set(entrypoints.map(validateComponentEntrypoint))].sort((left, right) => left.localeCompare(right))
}

export function deriveComponentCapabilityRequirements(
    input: DeriveComponentCapabilityRequirementsInput = {},
): ComponentCapabilityRequirements {
    const imports = [...(input.imports ?? [])]
    const sourceText = normalizeCapabilitySourceText(input.sourceText)
    const requiresNetwork = input.requiresNetwork === true || input.usesFetch === true
    const requiresSqlite = input.requiresSqlite === true
        || matchesAnyImport(imports, SQLITE_IMPORT_PATTERNS)
        || matchesAnySourcePattern(sourceText, SQLITE_SOURCE_PATTERNS)
    const requiresLlm = input.requiresLlm === true
        || matchesAnyImport(imports, LLM_IMPORT_PATTERNS)
        || matchesAnySourcePattern(sourceText, LLM_SOURCE_PATTERNS)
    const requiresMcp = input.requiresMcp === true
        || matchesAnyImport(imports, MCP_IMPORT_PATTERNS)
        || matchesAnySourcePattern(sourceText, MCP_SOURCE_PATTERNS)

    return {
        requiresSqlite,
        requiresLlm,
        requiresMcp,
        requiresNetwork,
        requiresAsyncHost: input.requiresAsyncHost === true || requiresSqlite || requiresLlm || requiresMcp || requiresNetwork,
    }
}

function normalizeCapabilitySourceText(sourceText: string | Iterable<string> | undefined): string[] {
    if (typeof sourceText === 'undefined') {
        return []
    }
    if (typeof sourceText === 'string') {
        return [sourceText]
    }
    return [...sourceText]
}

function matchesAnySourcePattern(sourceText: readonly string[], patterns: readonly RegExp[]): boolean {
    return sourceText.some(source => patterns.some(pattern => pattern.test(source)))
}

export function recommendComponentServerEngine(
    requirements: Partial<ComponentCapabilityRequirements> = {},
): ComponentServerEngine {
    void requirements
    return 'wasmtime'
}

export function createComponentArtifactId(input: ComponentArtifactIdentityInput): string {
    const normalized = normalizeArtifactIdentityInput(input)
    return bytesToHex(nobleSha256(encoder.encode(canonicalJsonStringify(normalized))))
}

export function createComponentArtifactManifest(input: CreateComponentArtifactManifestInput): ComponentArtifactManifest {
    const artifactId = createComponentArtifactId(input)
    const requirements = deriveComponentCapabilityRequirements(input)
    const recommendedServerEngine = input.recommendedServerEngine && input.recommendedServerEngine !== 'auto'
        ? input.recommendedServerEngine
        : recommendComponentServerEngine(requirements)

    return {
        artifactId,
        sourceSnapshot: validateRequiredString(input.sourceSnapshot, 'sourceSnapshot'),
        buildTarget: normalizeComponentBuildTarget(input.entrypoints, input.buildTarget),
        entrypoints: normalizeComponentEntrypoints(input.entrypoints),
        createdAt: validateRequiredString(input.createdAt, 'createdAt'),
        worldVersion: validateRequiredString(input.worldVersion, 'worldVersion'),
        bindingFamily: validateRequiredString(input.bindingFamily ?? 'secureblob.component-sdk/v1', 'bindingFamily'),
        guestSdk: normalizeGuestSdkInfo(input.guestSdk),
        toolchain: normalizeToolchain(input.toolchain),
        hostCapabilities: normalizeHostCapabilities(input.hostCapabilities),
        sdkSurfaces: normalizeSdkSurfaces(input.sdkSurfaces),
        requiresSqlite: requirements.requiresSqlite,
        requiresLlm: requirements.requiresLlm,
        requiresMcp: requirements.requiresMcp,
        requiresNetwork: requirements.requiresNetwork,
        requiresAsyncHost: requirements.requiresAsyncHost,
        recommendedServerEngine,
        runnerCompatibility: normalizeRunnerCompatibility(input.runnerCompatibility),
        files: normalizeArtifactFiles(input.files ?? []),
    }
}

export function deriveComponentBuildTarget(entrypoints: readonly string[]): ComponentBuildTarget {
    const normalizedEntrypoints = normalizeComponentEntrypoints(entrypoints)
    if (normalizedEntrypoints.length === 1) {
        return {
            kind: 'single-file',
            declaredPath: normalizedEntrypoints[0],
        }
    }

    return {
        kind: 'job-root',
        declaredPath: deriveEntrypointCommonDirectory(normalizedEntrypoints),
    }
}

export function createDefaultComponentJobSettings(
    options: NormalizeComponentJobSettingsOptions = {},
): ComponentJobSettings {
    return {
        enabled: false,
        target: 'server',
        schedulePreset: 'manual',
        cron: '',
        timezone: normalizeComponentJobTimezone(options.fallbackTimezone),
        payloadPreset: '',
        enginePolicy: 'auto',
        concurrencyPolicy: 'skip-if-running',
    }
}

export function normalizeComponentJobSettings(
    input: Partial<ComponentJobSettings> = {},
    options: NormalizeComponentJobSettingsOptions = {},
): ComponentJobSettings {
    const defaults = createDefaultComponentJobSettings(options)
    const schedulePreset = isAllowedValue(input.schedulePreset, COMPONENT_JOB_SCHEDULE_PRESETS)
        ? input.schedulePreset
        : defaults.schedulePreset

    return {
        enabled: typeof input.enabled === 'boolean' ? input.enabled : defaults.enabled,
        target: 'server',
        schedulePreset,
        cron: schedulePreset === 'custom' ? normalizeOptionalString(input.cron) : '',
        timezone: normalizeComponentJobTimezone(input.timezone ?? options.fallbackTimezone),
        payloadPreset: typeof input.payloadPreset === 'string' ? input.payloadPreset : defaults.payloadPreset,
        enginePolicy: isAllowedValue(input.enginePolicy, COMPONENT_JOB_ENGINE_POLICIES)
            ? input.enginePolicy
            : defaults.enginePolicy,
        concurrencyPolicy: isAllowedValue(input.concurrencyPolicy, COMPONENT_JOB_CONCURRENCY_POLICIES)
            ? input.concurrencyPolicy
            : defaults.concurrencyPolicy,
    }
}

export function parseComponentJobSettings(
    text: string | null | undefined,
    options: NormalizeComponentJobSettingsOptions = {},
): ComponentJobSettings {
    if (typeof text !== 'string' || text.trim().length === 0) {
        return createDefaultComponentJobSettings(options)
    }

    try {
        const value = JSON.parse(text) as unknown
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            if (
                'type' in value
                && (value as { type?: unknown }).type === 'component-job-config'
                && 'schedule' in value
                && (value as { schedule?: unknown }).schedule
                && typeof (value as { schedule?: unknown }).schedule === 'object'
                && !Array.isArray((value as { schedule?: unknown }).schedule)
            ) {
                return normalizeComponentJobSettings(
                    (value as { schedule: Partial<ComponentJobSettings> }).schedule,
                    options,
                )
            }
            return normalizeComponentJobSettings(value as Partial<ComponentJobSettings>, options)
        }
    } catch {
        return createDefaultComponentJobSettings(options)
    }

    return createDefaultComponentJobSettings(options)
}

export function serializeComponentJobSettings(
    input: Partial<ComponentJobSettings> = {},
    options: NormalizeComponentJobSettingsOptions = {},
): string {
    return canonicalJsonStringify(normalizeComponentJobSettings(input, options))
}

export function createDefaultComponentJobConfig(
    options: NormalizeComponentJobConfigOptions = {},
): ComponentJobConfig {
    return {
        v: '1',
        type: 'component-job-config',
        schedule: createDefaultComponentJobSettings(options),
    }
}

export function normalizeComponentJobConfig(
    input: Partial<Omit<ComponentJobConfig, 'entry' | 'schedule' | 'serverRunner'>> & {
        entry?: unknown
        schedule?: Partial<ComponentJobSettings>
        serverRunner?: unknown
    } = {},
    options: NormalizeComponentJobConfigOptions = {},
): ComponentJobConfig {
    const normalizedSchedule = normalizeComponentJobSettings(input.schedule, options)
    const normalizedEntry = normalizeComponentJobEntry(input.entry)
    const normalizedServerRunner = normalizeComponentJobServerRunner(input.serverRunner)

    return {
        v: '1',
        type: 'component-job-config',
        ...(normalizedEntry ? { entry: normalizedEntry } : {}),
        schedule: normalizedSchedule,
        ...(normalizedServerRunner ? { serverRunner: normalizedServerRunner } : {}),
    }
}

export function parseComponentJobConfig(
    text: string | null | undefined,
    options: NormalizeComponentJobConfigOptions = {},
): ComponentJobConfig {
    if (typeof text !== 'string' || text.trim().length === 0) {
        return createDefaultComponentJobConfig(options)
    }

    try {
        const value = JSON.parse(text) as unknown
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            if ('type' in value && (value as { type?: unknown }).type === 'component-job-config') {
                return normalizeComponentJobConfig(
                    value as Partial<Omit<ComponentJobConfig, 'entry' | 'schedule' | 'serverRunner'>> & {
                        entry?: unknown
                        schedule?: Partial<ComponentJobSettings>
                        serverRunner?: unknown
                    },
                    options,
                )
            }

            return normalizeComponentJobConfig(
                { schedule: value as Partial<ComponentJobSettings> },
                options,
            )
        }
    } catch {
        return createDefaultComponentJobConfig(options)
    }

    return createDefaultComponentJobConfig(options)
}

export function serializeComponentJobConfig(
    input: Partial<Omit<ComponentJobConfig, 'entry' | 'schedule' | 'serverRunner'>> & {
        entry?: unknown
        schedule?: Partial<ComponentJobSettings>
        serverRunner?: unknown
    } = {},
    options: NormalizeComponentJobConfigOptions = {},
): string {
    return canonicalJsonStringify(normalizeComponentJobConfig(input, options))
}

export function resolveComponentJobScheduleCron(
    input: Partial<ComponentJobSettings> = {},
    options: NormalizeComponentJobSettingsOptions = {},
): string | null {
    const settings = normalizeComponentJobSettings(input, options)

    switch (settings.schedulePreset) {
        case 'manual':
            return null
        case 'every-5-min':
            return '*/5 * * * *'
        case 'hourly':
            return '0 * * * *'
        case 'daily':
            return '0 0 * * *'
        case 'custom':
            return settings.cron || null
        default:
            return null
    }
}

export function isBlankComponentJobSource(text: string | null | undefined): boolean {
    return typeof text !== 'string' || text.trim().length === 0
}

export function createComponentJobStarterSource(
    sourcePath: string,
    options: CreateComponentJobStarterSourceOptions = {},
): string {
    const normalizedSourcePath = validateComponentEntrypoint(sourcePath)
    const template = options.template === 'container-service'
        ? options.template
        : 'container-service'

    switch (template) {
        case 'container-service':
        default:
            return [
                "import { openWorkspaceFromRunnerBootstrap } from '@secureblob/sdk/componentJobRuntime'",
                '',
                'interface ContainerServiceInput {',
                '    serviceName?: string',
                '    route?: string',
                '    healthcheckUrl?: string',
                '}',
                '',
                '// Starter for a file-first server job. Build or Run in SecureBlob will',
                '// provision the companion config and server-runner automatically.',
                'export default async function main(input: ContainerServiceInput = {}) {',
                "    const { runtime, bootstrap } = await openWorkspaceFromRunnerBootstrap(undefined, { workspaceSync: { mode: 'on-demand' } })",
                `    const defaultServiceName = ${JSON.stringify(deriveStarterServiceName(normalizedSourcePath))}`,
                '    const serviceName = typeof input.serviceName === \'string\' && input.serviceName.trim().length > 0',
                '        ? input.serviceName.trim()',
                '        : defaultServiceName',
                '    const route = typeof input.route === \'string\' && input.route.trim().length > 0',
                '        ? input.route.trim()',
                '        : \'/health\'',
                '',
                '    let upstream: { target: string; status: number; ok: boolean } | null = null',
                '    if (typeof input.healthcheckUrl === \'string\' && input.healthcheckUrl.trim().length > 0) {',
                '        const target = input.healthcheckUrl.trim()',
                '        const response = await runtime.fetch(target, { method: \'GET\' })',
                '        upstream = {',
                '            target,',
                '            status: response.status,',
                '            ok: response.ok,',
                '        }',
                '    }',
                '',
                '    return {',
                '        service: serviceName,',
                '        route,',
                '        runtime: \'secureblob-component-job\',',
                '        workspace: bootstrap.workspaceAlias ?? bootstrap.workspaceBlobId ?? \'workspace\',',
                '        apiBaseUrl: bootstrap.apiBaseUrl,',
                '        message: `${serviceName} is ready for Build and Run.`,',
                '        upstream,',
                '        next: [',
                '            \'Build to produce component.wasm\',',
                '            \'Run on server to execute this starter payload\',',
                '            \'Enable Schedule when you want recurring checks\',',
                '        ],',
                '    }',
                '}',
                '',
            ].join('\n')
    }
}

/**
 * Produce a multi-file scaffold definition for the given template.
 *
 * The scaffold is designed to be consumed by FileBrowser, ComponentJobPanel,
 * or any other surface that needs to materialize starter files into a
 * folder-first job root.
 *
 * The first file in `files` is always the primary entrypoint (`main.tsx`).
 *
 * §106 contract: same scaffold definition shared across SDK and webapp.
 */
export function createComponentJobScaffold(
    jobRootPath: string,
    options: CreateComponentJobScaffoldOptions = {},
): ComponentJobScaffold {
    const normalizedJobRoot = validatePath(jobRootPath)
    if (!normalizedJobRoot.endsWith('.job')) {
        throw new SdkError('INVALID_PATH', 'Scaffold job root path must end with .job')
    }

    const template = isAllowedValue(options.template, COMPONENT_JOB_STARTER_TEMPLATES)
        ? options.template
        : 'container-service'

    const entrypointPath = `${normalizedJobRoot}/main.tsx`

    switch (template) {
        case 'minimal':
            return {
                template: 'minimal',
                label: 'Minimal',
                description: 'A bare-bones starter with just the runtime bootstrap and a simple return value.',
                files: [
                    {
                        path: 'main.tsx',
                        content: createMinimalStarterSource(entrypointPath),
                    },
                    {
                        path: 'main.test.ts',
                        content: createMinimalTestSource(entrypointPath),
                    },
                ],
                directories: [],
            }

        case 'multi-file-service':
            return {
                template: 'multi-file-service',
                label: 'Multi-file Service',
                description: 'A container service starter with a lib/ directory for helper modules and a templates/ directory for output templates.',
                files: [
                    {
                        path: 'main.tsx',
                        content: createMultiFileServiceMainSource(entrypointPath),
                    },
                    {
                        path: 'lib/helpers.ts',
                        content: createHelpersModuleSource(),
                    },
                    {
                        path: 'lib/types.ts',
                        content: createTypesModuleSource(),
                    },
                    {
                        path: 'main.test.ts',
                        content: createMultiFileServiceTestSource(entrypointPath),
                    },
                ],
                directories: ['lib', 'templates'],
            }

        case 'container-service':
        default:
            return {
                template: 'container-service',
                label: 'Container Service',
                description: 'The default container-service starter that checks upstream health and reports status.',
                files: [
                    {
                        path: 'main.tsx',
                        content: createComponentJobStarterSource(entrypointPath, { template: 'container-service' }),
                    },
                    {
                        path: 'main.test.ts',
                        content: createContainerServiceTestSource(entrypointPath),
                    },
                ],
                directories: [],
            }
    }
}

function createMinimalStarterSource(sourcePath: string): string {
    const serviceName = deriveStarterServiceName(sourcePath)
    return [
        "import { openWorkspaceFromRunnerBootstrap } from '@secureblob/sdk/componentJobRuntime'",
        '',
        '// Minimal starter: opens the workspace from runner bootstrap and returns a simple result.',
        '// Build or Run in SecureBlob will provision the companion config automatically.',
        'export default async function main() {',
        "    const { bootstrap } = await openWorkspaceFromRunnerBootstrap(undefined, { workspaceSync: { mode: 'on-demand' } })",
        '',
        '    return {',
        `        service: ${JSON.stringify(serviceName)},`,
        '        status: \'ready\',',
        '        workspace: bootstrap.workspaceAlias ?? bootstrap.workspaceBlobId ?? \'workspace\',',
        '        message: `Minimal starter running. Add your logic here.`',
        '    }',
        '}',
        '',
    ].join('\n')
}

function createMultiFileServiceMainSource(sourcePath: string): string {
    const serviceName = deriveStarterServiceName(sourcePath)
    return [
        "import { openWorkspaceFromRunnerBootstrap } from '@secureblob/sdk/componentJobRuntime'",
        "import { formatServiceResult, resolveTarget } from './lib/helpers'",
        "import type { ServiceCheckInput } from './lib/types'",
        '',
        '// Multi-file service starter with lib/ support modules.',
        '// Build or Run in SecureBlob will provision the companion config automatically.',
        'export default async function main(input: ServiceCheckInput = {}) {',
        "    const { runtime, bootstrap } = await openWorkspaceFromRunnerBootstrap(undefined, { workspaceSync: { mode: 'on-demand' } })",
        `    const defaultServiceName = ${JSON.stringify(serviceName)}`,
        '    const serviceName = input.serviceName?.trim() || defaultServiceName',
        '    const target = resolveTarget(input.healthcheckUrl, input.route)',
        '',
        '    let upstream: { target: string; status: number; ok: boolean } | null = null',
        '    if (target) {',
        '        const response = await runtime.fetch(target, { method: \'GET\' })',
        '        upstream = { target, status: response.status, ok: response.ok }',
        '    }',
        '',
        '    return formatServiceResult({',
        '        serviceName,',
        '        workspace: bootstrap.workspaceAlias ?? bootstrap.workspaceBlobId ?? \'workspace\',',
        '        apiBaseUrl: bootstrap.apiBaseUrl,',
        '        upstream,',
        '    })',
        '}',
        '',
    ].join('\n')
}

function createHelpersModuleSource(): string {
    return [
        "import type { ServiceResult, ServiceCheckInput } from './types'",
        '',
        'export function resolveTarget(',
        '    healthcheckUrl?: string,',
        "    route?: string,",
        '): string | null {',
        "    if (healthcheckUrl && healthcheckUrl.trim().length > 0) return healthcheckUrl.trim()",
        '    return null',
        '}',
        '',
        'export function formatServiceResult({',
        '    serviceName,',
        '    workspace,',
        '    apiBaseUrl,',
        '    upstream,',
        '}: {',
        '    serviceName: string',
        '    workspace: string',
        '    apiBaseUrl: string',
        '    upstream: { target: string; status: number; ok: boolean } | null',
        '}): ServiceResult {',
        '    return {',
        '        service: serviceName,',
        '        runtime: \'secureblob-component-job\',',
        '        workspace,',
        '        apiBaseUrl,',
        '        message: `${serviceName} is ready for Build and Run.`,',
        '        upstream,',
        '        next: [',
        '            \'Build to produce component.wasm\',',
        '            \'Run on server to execute this starter payload\',',
        '            \'Enable Schedule when you want recurring checks\',',
        '        ],',
        '    }',
        '}',
        '',
    ].join('\n')
}

function createTypesModuleSource(): string {
    return [
        'export interface ServiceCheckInput {',
        '    serviceName?: string',
        '    route?: string',
        '    healthcheckUrl?: string',
        '}',
        '',
        'export interface ServiceResult {',
        '    service: string',
        '    runtime: string',
        '    workspace: string',
        '    apiBaseUrl: string',
        '    message: string',
        '    upstream: { target: string; status: number; ok: boolean } | null',
        '    next: string[]',
        '}',
        '',
    ].join('\n')
}

function createMinimalTestSource(sourcePath: string): string {
    const serviceName = deriveStarterServiceName(sourcePath)
    return [
        '/**',
        ' * Minimal starter test — §439',
        ' *',
        ' * Uses `createJobTestHarness` from `@secureblob/sdk/testing` to run',
        ' * the job logic against an in-memory workspace without any live server,',
        ' * MCP connection, or Wasmtime runtime.',
        ' */',
        "import { createJobTestHarness } from '@secureblob/sdk/testing'",
        "import type { InMemoryWorkspace } from '@secureblob/sdk/testing'",
        '',
        'describe(\'MinimalJob\', () => {',
        '    it(\'returns ready status with workspace info\', async () => {',
        '        const harness = createJobTestHarness()',
        '',
        '        await harness.run(async (ws: InMemoryWorkspace) => {',
        '            // Seed any input files your job needs',
        '            // ws.fs.writeText(\'/input/data.json\', JSON.stringify({ key: \'value\' }))',
        '        })',
        '',
        '        // Assert on runSummary or statusCards as needed',
        '        // expect(harness.runSummary?.status).toBe(\'succeeded\')',
        '        // expect(harness.statusCards).toHaveLength(1)',
        '        expect(harness.workspace).toBeDefined()',
        '    })',
        '})',
        '',
    ].join('\n')
}

function createMultiFileServiceTestSource(sourcePath: string): string {
    const serviceName = deriveStarterServiceName(sourcePath)
    return [
        '/**',
        ' * Multi-file service test — §439',
        ' *',
        ' * Uses `createJobTestHarness` from `@secureblob/sdk/testing` to test',
        ' * the service logic with seeded input files.',
        ' */',
        "import { createJobTestHarness } from '@secureblob/sdk/testing'",
        "import type { InMemoryWorkspace } from '@secureblob/sdk/testing'",
        '',
        'describe(\'MultiFileServiceJob\', () => {',
        '    it(\'runs with seeded inputs and produces expected outputs\', async () => {',
        '        const harness = createJobTestHarness({',
        `            seedFiles: { '/input/service-input.json': JSON.stringify({ serviceName: ${JSON.stringify(serviceName)} }) }`,
        '        })',
        '',
        '        await harness.run(async (ws: InMemoryWorkspace) => {',
        '            // Your job logic here: read inputs, produce outputs',
        '            // const input = await ws.fs.readText(\'/input/service-input.json\')',
        '            // ws.fs.writeText(\'/output/result.json\', JSON.stringify({ processed: true }))',
        '        })',
        '',
        '        // Assert on runSummary or statusCards as needed',
        '        // expect(harness.runSummary?.status).toBe(\'succeeded\')',
        '        expect(harness.workspace).toBeDefined()',
        '    })',
        '})',
        '',
    ].join('\n')
}

function createContainerServiceTestSource(sourcePath: string): string {
    const serviceName = deriveStarterServiceName(sourcePath)
    return [
        '/**',
        ' * Container service test — §439',
        ' *',
        ' * Uses `createJobTestHarness` from `@secureblob/sdk/testing` to test',
        ' * the container service logic (healthcheck, status reporting).',
        ' */',
        "import { createJobTestHarness } from '@secureblob/sdk/testing'",
        "import type { InMemoryWorkspace } from '@secureblob/sdk/testing'",
        '',
        'interface ContainerServiceInput {',
        '    serviceName?: string',
        '    route?: string',
        '    healthcheckUrl?: string',
        '}',
        '',
        'describe(\'ContainerServiceJob\', () => {',
        '    it(\'produces a run summary with succeeded status\', async () => {',
        '        const harness = createJobTestHarness()',
        '',
        '        await harness.run(async (ws: InMemoryWorkspace) => {',
        '            // Seed any upstream healthcheck response or input data',
        '            // ws.fs.writeText(\'/mock/upstream-health.json\', JSON.stringify({ ok: true, status: 200 }))',
        '        })',
        '',
        '        // Assert on runSummary',
        '        // expect(harness.runSummary?.status).toBe(\'succeeded\')',
        '        expect(harness.workspace).toBeDefined()',
        '    })',
        '',
        '    it(\'emits status cards during execution\', async () => {',
        '        const harness = createJobTestHarness()',
        '',
        '        await harness.run(async (ws: InMemoryWorkspace) => {',
        '            // Write status cards to /.status-cards/ to simulate SDK calls',
        '            // ws.fs.writeText(\'/.status-cards/progress.json\', JSON.stringify({',
        '            //     id: \'progress\',',
        '            //     title: \'Running\',',
        '            //     status: \'running\',',
        '            //     tone: \'info\',',
        '            //     detail: \'Processing...\',',
        '            // }))',
        '        })',
        '',
        '        // Assert on statusCards',
        '        // expect(harness.statusCards.length).toBeGreaterThan(0)',
        '    })',
        '})',
        '',
    ].join('\n')
}

export function getComponentJobCompanionPaths(sourcePath: string): ComponentJobCompanionPaths {
    const normalizedSourcePath = validateComponentEntrypoint(sourcePath)
    const folderFirstRoot = getFolderFirstComponentJobRootFromEntrypoint(normalizedSourcePath)

    if (folderFirstRoot) {
        return {
            settings: validatePath(`${folderFirstRoot}/job.json`),
            runsDir: validatePath(`${folderFirstRoot}/.secureblob/runs`),
            buildsDir: validatePath(`${folderFirstRoot}/.secureblob/artifacts`),
        }
    }

    return {
        settings: validatePath(`${normalizedSourcePath}.job.json`),
        runsDir: validatePath(`${normalizedSourcePath}.runs`),
        buildsDir: validatePath(`${normalizedSourcePath}.builds`),
    }
}

export function getComponentRunPaths(sourcePath: string, runId: string): ComponentRunPaths {
    validateName(runId)

    const { runsDir } = getComponentJobCompanionPaths(sourcePath)
    const runDir = validatePath(`${runsDir}/${runId}`)

    return {
        runDir,
        metadata: validatePath(`${runDir}/run.json`),
        stdout: validatePath(`${runDir}/stdout.log`),
        stderr: validatePath(`${runDir}/stderr.log`),
        events: validatePath(`${runDir}/events.jsonl`),
        http: validatePath(`${runDir}/http.jsonl`),
        mcp: validatePath(`${runDir}/mcp.jsonl`),
        statusCards: validatePath(`${runDir}/status-cards.jsonl`),
        outputsDir: validatePath(`${runDir}/outputs`),
    }
}

export function getComponentRunAgentSessionPaths(
    sourcePath: string,
    runId: string,
    sessionId: string,
): ComponentRunAgentSessionPaths {
    validateName(sessionId)

    const runPaths = getComponentRunPaths(sourcePath, runId)
    const sessionsDir = validatePath(`${runPaths.outputsDir}/agent-sessions`)
    const sessionDir = validatePath(`${sessionsDir}/${sessionId}`)

    return {
        sessionsDir,
        sessionDir,
        transcript: validatePath(`${sessionDir}/transcript.json`),
    }
}

export function getComponentArtifactPaths(artifactDir: string): ComponentArtifactPaths {
    const normalizedArtifactDir = validateBuildArtifactRoot(artifactDir)

    return {
        artifactDir: normalizedArtifactDir,
        componentWasm: validatePath(`${normalizedArtifactDir}/component.wasm`),
        manifest: validatePath(`${normalizedArtifactDir}/manifest.json`),
        buildLog: validatePath(`${normalizedArtifactDir}/build-log.txt`),
    }
}

export function getComponentJobBuildPaths(sourcePath: string, artifactId: string): ComponentArtifactPaths {
    validateName(artifactId)

    const { buildsDir } = getComponentJobCompanionPaths(sourcePath)
    return getComponentArtifactPaths(validatePath(`${buildsDir}/${artifactId}`))
}

export function getComponentBuildAttemptPaths(sourcePath: string, buildId: string): ComponentBuildAttemptPaths {
    validateName(buildId)

    const { buildsDir } = getComponentJobCompanionPaths(sourcePath)
    const attemptsDir = validatePath(`${buildsDir}/_attempts`)
    const attemptDir = validatePath(`${attemptsDir}/${buildId}`)
    return {
        attemptsDir,
        attemptDir,
        metadata: validatePath(`${attemptDir}/build.json`),
        buildLog: validatePath(`${attemptDir}/build-log.txt`),
        outputsDir: validatePath(`${attemptDir}/outputs`),
    }
}

export function getComponentJobRootPath(path: string): string | null {
    let normalized: string
    try {
        normalized = validatePath(path)
    } catch {
        return null
    }

    if (normalized.endsWith('.job')) {
        return normalized
    }

    const folderRootMatch = normalized.match(/^(.*?\.job)(?:\/(?:job\.json|main\.[^/]+)|\/\.secureblob(?:\/.*)?$)/)
    if (folderRootMatch) {
        return folderRootMatch[1]
    }

    return null
}

export function getComponentJobSourcePath(path: string): string | null {
    let normalized: string
    try {
        normalized = validatePath(path)
    } catch {
        return null
    }

    const folderFirstRoot = getComponentJobRootPath(normalized)
    if (folderFirstRoot) {
        // If the path is a main.* entrypoint inside the .job root, return it directly
        if (normalized.length > folderFirstRoot.length) {
            const relative = normalized.slice(folderFirstRoot.length + 1)
            if (COMPONENT_JOB_SOURCE_EXTENSIONS.some(ext => relative === `main${ext}`)) {
                return normalized
            }
        }
        // For the .job root itself or companion paths, default to main.tsx
        return validateComponentEntrypoint(`${folderFirstRoot}/main.tsx`)
    }

    if (isComponentJobEntrypointPath(normalized)) {
        return normalized
    }

    const derived = deriveSourcePathFromComponentCompanion(normalized)
    return derived && isComponentJobEntrypointPath(derived) ? derived : null
}

export function isComponentJobPath(path: string): boolean {
    return getComponentJobSourcePath(path) !== null
}

export function isComponentJobInternalPath(path: string): boolean {
    const parsed = parseComponentJobPath(path)
    return parsed !== null && parsed.kind !== 'source'
}

export function parseComponentJobPath(path: string): ParsedComponentJobPath | null {
    const normalized = validatePath(path)
    const sourcePath = getComponentJobSourcePath(normalized)
    if (sourcePath === null) {
        return null
    }

    const folderFirstRoot = getComponentJobRootPath(normalized)
    if (folderFirstRoot && normalized === folderFirstRoot) {
        return { path: normalized, sourcePath, kind: 'job_root' }
    }

    if (normalized === sourcePath) {
        return { path: normalized, sourcePath, kind: 'source' }
    }

    const { settings, runsDir, buildsDir } = getComponentJobCompanionPaths(sourcePath)
    if (normalized === settings) {
        return { path: normalized, sourcePath, kind: 'settings' }
    }
    if (normalized === runsDir) {
        return { path: normalized, sourcePath, kind: 'runs_dir' }
    }
    if (normalized === buildsDir) {
        return { path: normalized, sourcePath, kind: 'builds_dir' }
    }
    if (normalized.startsWith(runsDir + '/')) {
        return parseRunPath(normalized, sourcePath, runsDir)
    }
    if (normalized.startsWith(buildsDir + '/')) {
        return parseBuildPath(normalized, sourcePath, buildsDir)
    }

    return null
}

export async function prepareBuild(options: PrepareBuildOptions): Promise<ComponentBuildStatusRecord> {
    const response = await requestJson(
        '/builds/prepare',
        {
            method: 'POST',
            body: JSON.stringify({
                sourceSnapshot: validateRequiredString(options.sourceSnapshot, 'sourceSnapshot'),
                entrypoints: normalizeComponentEntrypoints(options.entrypoints),
                workspaceBlobId: validateRequiredString(options.workspaceBlobId, 'workspaceBlobId'),
                runnerBootstrap: normalizeRequiredComponentRunnerBootstrap(options.runnerBootstrap, 'runnerBootstrap'),
            }),
        },
        options,
    )

    return parseBuildStatusRecord(response)
}

export async function getBuildStatus(options: GetBuildStatusOptions): Promise<ComponentBuildStatusRecord> {
    const buildId = validateRouteName(options.buildId, 'buildId')
    const response = await requestJson(`/builds/${encodeURIComponent(buildId)}/status`, {}, options)
    return parseBuildStatusRecord(response)
}

export async function downloadBuildFile(options: DownloadBuildFileOptions): Promise<Uint8Array> {
    const buildId = validateRouteName(options.buildId, 'buildId')
    const fileName = validateRelativeArtifactPath(options.fileName)
    return requestBytes(`/builds/${encodeURIComponent(buildId)}/files/${encodeArtifactFilePath(fileName)}`, {}, options)
}

export async function materializeBuild(options: MaterializeBuildOptions): Promise<ComponentBuildStatusRecord> {
    const buildId = validateRouteName(options.buildId, 'buildId')
    const response = await requestJson(
        `/builds/${encodeURIComponent(buildId)}/materialize`,
        {
            method: 'POST',
            body: JSON.stringify({
                buildId,
                targetPath: validatePath(options.targetPath),
                fileNames: options.fileNames?.map(validateRelativeArtifactPath),
            }),
        },
        options,
    )

    return parseBuildStatusRecord(response)
}

export async function inspectArtifact(options: InspectArtifactOptions): Promise<ComponentArtifactManifest> {
    const artifactId = validateRouteName(options.artifactId, 'artifactId')
    const response = await requestJson(`/artifacts/${encodeURIComponent(artifactId)}`, {}, options)
    return response as ComponentArtifactManifest
}

export async function downloadArtifactFile(options: DownloadArtifactFileOptions): Promise<Uint8Array> {
    const artifactId = validateRouteName(options.artifactId, 'artifactId')
    const fileName = validateRelativeArtifactPath(options.fileName)
    return requestBytes(`/artifacts/${encodeURIComponent(artifactId)}/files/${encodeArtifactFilePath(fileName)}`, {}, options)
}

/**
 * Options for `runServer` with lifecycle hooks.
 * Extends `RunServerOptions` with an optional `hooks` field.
 */
export interface RunServerWithHooksOptions extends RunServerOptions {
    /** Optional lifecycle hooks to invoke at each stage transition. */
    hooks?: ComponentJobLifecycleHooks
}

/**
 * Submit a component run and return the initial status record.
 * Alias of `runServer(options, undefined)` — no hooks invoked.
 */
export async function runServer(options: RunServerOptions): Promise<ComponentRunStatusRecord>
/**
 * Full lifecycle run: prepare build → materialize → run → poll to completion,
 * invoking <paramref name="hooks"/> at each stage transition.
 * Mirrors the .NET `IComponentJobClient.RunAsync` contract (§269).
 */
export async function runServer(
    options: RunServerWithHooksOptions,
    hooks?: ComponentJobLifecycleHooks,
): Promise<ComponentRunStatusRecord>
export async function runServer(
    options: RunServerOptions | RunServerWithHooksOptions,
    hooks?: ComponentJobLifecycleHooks,
): Promise<ComponentRunStatusRecord> {
    // If hooks were passed as second argument, delegate to runServerWithHooks
    if (hooks !== undefined) {
        return runServerWithHooks(options as RunServerOptions, hooks)
    }
    // Otherwise fall through to plain run (no hooks)
    const response = await requestJson(
        '/runs',
        {
            method: 'POST',
            body: JSON.stringify({
                artifactId: validateRouteName(options.artifactId, 'artifactId'),
                workspaceBlobId: validateRequiredString(options.workspaceBlobId, 'workspaceBlobId'),
                engine: options.engine ?? 'auto',
                runInput: options.runInput ?? null,
                sourcePath: options.sourcePath ? validateComponentEntrypoint(options.sourcePath) : null,
                runnerBootstrap: options.runnerBootstrap ? normalizeComponentRunnerBootstrap(options.runnerBootstrap) : null,
                jobRoot: options.jobRoot ? validatePath(options.jobRoot) : null,
            }),
        },
        options,
    )

    return parseRunStatusRecord(response)
}

/**
 * Full lifecycle run: prepare build → materialize → run → poll to completion,
 * invoking <paramref name="lifecycleHooks"/> at each stage transition.
 * Mirrors the .NET `IComponentJobClient.RunAsync` contract (§269).
 */
export async function runServerWithHooks(
    options: RunServerOptions,
    lifecycleHooks?: ComponentJobLifecycleHooks,
): Promise<ComponentRunStatusRecord> {
    const hooks = lifecycleHooks
    const sourcePath = options.sourcePath ?? 'unknown'

    try {
        // Stage 1: Pre-build
        hooks?.onPreBuild?.(sourcePath)

        // Stage 2: Prepare build
        const build = await prepareBuild({
            ...options,
            sourceSnapshot: options.artifactId,
            entrypoints: options.sourcePath ? [options.sourcePath] : [],
            runnerBootstrap: options.runnerBootstrap ?? { apiBaseUrl: '' },
        })

        // Poll build until terminal
        const buildStatus = await pollBuild(build.buildId, options)
        if (buildStatus.stage === 'failed') {
            hooks?.onError?.('build', new SdkError('BUILD_FAILED', `Build failed: ${buildStatus.error ?? 'unknown'}`))
            throw new SdkError('BUILD_FAILED', `Build ${build.buildId} failed: ${buildStatus.error ?? 'unknown'}`)
        }

        // Stage 3: Post-materialize
        const materialized = await materializeBuild({
            buildId: build.buildId,
            targetPath: `/runs/${crypto.randomUUID()}`,
            ...options,
        })

        const manifest = await inspectArtifact({ ...options, artifactId: materialized.artifactId! })
        hooks?.onPostMaterialize?.(materialized.artifactId!, manifest)

        // Stage 4: Pre-run
        const run = await runServer({
            ...options,
            artifactId: materialized.artifactId!,
            engine: options.engine,
            runInput: options.runInput,
            sourcePath: options.sourcePath,
            runnerBootstrap: options.runnerBootstrap,
            jobRoot: options.jobRoot,
        })
        hooks?.onPreRun?.(run.runId)

        // Stage 5: Poll run to terminal
        const finalRun = await pollRun(run.runId, options)
        hooks?.onPostRun?.(finalRun.runId, finalRun)

        return finalRun
    }
    catch (error) {
        const err = error instanceof Error ? error : new Error(String(error))
        // Only fire onError for lifecycle errors that are NOT from the build-failure
        // path (which already called onError in the if-block above).
        if (!(err instanceof SdkError && err.code === 'BUILD_FAILED')) {
            hooks?.onError?.('lifecycle', err)
        }
        throw err
    }
}

async function pollBuild(buildId: string, options: ComponentJobsClientOptions, timeoutMs = 60000, pollIntervalMs = 500): Promise<ComponentBuildStatusRecord> {
    const deadline = Date.now() + timeoutMs
    while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, pollIntervalMs))
        const status = await getBuildStatus({ buildId, ...options })
        if (status.stage === 'ready' || status.stage === 'failed') return status
    }
    throw new SdkError('TIMEOUT', `Build ${buildId} did not complete within ${timeoutMs}ms`)
}

async function pollRun(runId: string, options: ComponentJobsClientOptions, timeoutMs = 60000, pollIntervalMs = 500): Promise<ComponentRunStatusRecord> {
    const deadline = Date.now() + timeoutMs
    while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, pollIntervalMs))
        const status = await getRunStatus({ runId, ...options })
        if (status.stage === 'completed' || status.stage === 'failed' || status.stage === 'cancelled') return status
    }
    throw new SdkError('TIMEOUT', `Run ${runId} did not reach a terminal state within ${timeoutMs}ms`)
}

export async function getRunStatus(options: GetRunStatusOptions): Promise<ComponentRunStatusRecord> {
    const runId = validateRouteName(options.runId, 'runId')
    const response = await requestJson(`/runs/${encodeURIComponent(runId)}`, {}, options)
    return parseRunStatusRecord(response)
}

export async function cancelRun(options: CancelRunOptions): Promise<ComponentRunStatusRecord> {
    const runId = validateRouteName(options.runId, 'runId')
    const response = await requestJson(`/runs/${encodeURIComponent(runId)}/cancel`, { method: 'POST' }, options)
    return parseRunStatusRecord(response)
}

export async function triggerRun(options: TriggerRunOptions): Promise<ComponentTriggerDispatchRecord> {
    const sourcePath = encodeSourcePathForRoute(validateComponentEntrypoint(options.sourcePath))
    const response = await requestJson(
        `/triggers/${sourcePath}`,
        {
            method: 'POST',
            body: JSON.stringify({
                workspaceBlobId: validateRequiredString(options.workspaceBlobId, 'workspaceBlobId'),
                engine: options.engine ?? 'auto',
                runInput: options.runInput ?? null,
                runnerBootstrap: options.runnerBootstrap ? normalizeComponentRunnerBootstrap(options.runnerBootstrap) : null,
            }),
        },
        options,
    )

    return parseTriggerDispatchRecord(response)
}

export async function callHandler(options: CallHandlerOptions): Promise<ComponentHandlerCallResult> {
    const sourcePath = encodeSourcePathForRoute(validateComponentEntrypoint(options.sourcePath))
    const timeoutSeconds = normalizeHandlerTimeoutSeconds(options.timeoutSeconds)
    const startedAt = Date.now()
    const response = await performRequest(
        `/handlers/${sourcePath}`,
        {
            method: 'POST',
            headers: {
                'X-Handler-Timeout': String(timeoutSeconds),
            },
            body: JSON.stringify({
                workspaceBlobId: validateRequiredString(options.workspaceBlobId, 'workspaceBlobId'),
                engine: options.engine ?? 'auto',
                inputJson: options.inputJson ?? null,
                runnerBootstrap: options.runnerBootstrap ? normalizeComponentRunnerBootstrap(options.runnerBootstrap) : null,
            }),
        },
        options,
    )
    const durationMs = Math.max(0, Date.now() - startedAt)
    const runIdFromHeader = readResponseHeader(response, 'x-secureblob-handler-run-id')

    if (response.status === 204) {
        return {
            statusCode: 204,
            durationMs,
            ...(runIdFromHeader ? { runId: runIdFromHeader } : {}),
        }
    }

    const payload = await readJsonPayload(response)
    const runId = extractHandlerRunId(payload) ?? runIdFromHeader

    if (response.status === 200) {
        return {
            statusCode: 200,
            durationMs,
            ...(runId ? { runId } : {}),
            output: payload,
        }
    }

    if (response.status === 400 || response.status === 401 || response.status === 408 || response.status === 422 || response.status === 499) {
        return {
            statusCode: response.status,
            durationMs,
            ...(runId ? { runId } : {}),
            ...(payload !== null ? { message: getHttpErrorMessage(response, payload) } : {}),
            ...(extractHandlerErrorCode(payload) ? { errorCode: extractHandlerErrorCode(payload) } : {}),
        }
    }

    if (!response.ok) {
        throw createHttpError(response, payload)
    }

    return {
        statusCode: response.status,
        durationMs,
        ...(runId ? { runId } : {}),
        output: payload,
    }
}

export async function downloadRunLog(options: DownloadRunLogOptions): Promise<string> {
    const runId = validateRouteName(options.runId, 'runId')
    const logName = validateRunLogName(options.logName)
    return requestText(`/runs/${encodeURIComponent(runId)}/logs/${encodeURIComponent(logName)}`, {}, options)
}

export async function* tailRunLog(options: TailRunLogOptions): AsyncGenerator<string, void, unknown> {
    const runId = validateRouteName(options.runId, 'runId')
    const logName = validateRunLogName(options.logName)
    const fromLine = typeof options.fromLine === 'number' && options.fromLine >= 0 ? options.fromLine : 0
    const fetchImpl = resolveFetchImpl(options)
    const baseUrl = normalizeComponentJobsApiBaseUrl(options.apiBaseUrl)
    const url = `${baseUrl}/runs/${encodeURIComponent(runId)}/logs/${encodeURIComponent(logName)}/tail${fromLine > 0 ? `?fromLine=${fromLine}` : ''}`
    const headers = mergeRequestHeaders(options.headers, {
        Accept: 'text/event-stream',
        'Cache-Control': 'no-cache',
    })

    const fetchOptions: RequestInit = {
        headers,
        signal: options.signal,
    }

    const response = await fetchImpl(url, fetchOptions)
    if (!response.ok) {
        throw createHttpError(response, await readJsonPayload(response))
    }

    if (!response.body) {
        throw new SdkError('NETWORK_ERROR', 'tailRunLog: response body is not available (browser may not support ReadableStream)')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
        while (true) {
            const { done, value } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const lines = buffer.split('\n')
            buffer = lines.pop() ?? ''

            for (const line of lines) {
                if (line.startsWith('data: ')) {
                    const data = line.slice('data: '.length)
                    if (data === '[DONE]') {
                        return
                    }
                    yield data
                }
            }
        }
    } finally {
        reader.cancel()
    }
}

export async function schedule(options: ScheduleOptions): Promise<ComponentJobScheduleRecord> {
    const normalizedSourcePath = encodeSourcePathForRoute(validateComponentEntrypoint(options.sourcePath))
    const normalizedSettings = normalizeComponentJobSettings({
        enabled: options.enabled,
        schedulePreset: options.schedulePreset,
        cron: options.cron,
        timezone: options.timezone,
        payloadPreset: options.payloadPreset ?? '',
        enginePolicy: options.enginePolicy,
        concurrencyPolicy: options.concurrencyPolicy,
    })

    const response = await requestJson(
        `/schedules/${normalizedSourcePath}`,
        {
            method: 'PUT',
            body: JSON.stringify({
                artifactId: validateRouteName(options.artifactId, 'artifactId'),
                workspaceBlobId: validateRequiredString(options.workspaceBlobId, 'workspaceBlobId'),
                enabled: normalizedSettings.enabled,
                schedulePreset: normalizedSettings.schedulePreset,
                cron: normalizedSettings.cron,
                timezone: normalizedSettings.timezone,
                enginePolicy: normalizedSettings.enginePolicy,
                concurrencyPolicy: normalizedSettings.concurrencyPolicy,
                payloadPreset: normalizedSettings.payloadPreset || null,
                runnerBootstrap: options.runnerBootstrap ? normalizeComponentRunnerBootstrap(options.runnerBootstrap) : null,
                jobRoot: options.jobRoot ? validatePath(options.jobRoot) : null,
            }),
        },
        options,
    )

    return parseScheduleRecord(response)
}

export async function getSchedules(options: GetSchedulesOptions): Promise<ComponentJobScheduleRecord[]> {
    const query = typeof options.workspaceBlobId === 'string' && options.workspaceBlobId.trim().length > 0
        ? `?workspaceBlobId=${encodeURIComponent(options.workspaceBlobId)}`
        : ''
    const response = await requestJson(`/schedules${query}`, {}, options)
    return parseScheduleRecordList(response)
}

export async function disableSchedule(options: DisableScheduleOptions): Promise<ComponentJobScheduleRecord> {
    const sourcePath = encodeSourcePathForRoute(validateComponentEntrypoint(options.sourcePath))
    const response = await requestJson(
        `/schedules/disable/${sourcePath}`,
        {
            method: 'POST',
            body: JSON.stringify({
                workspaceBlobId: validateRequiredString(options.workspaceBlobId, 'workspaceBlobId'),
            }),
        },
        options,
    )

    return parseScheduleRecord(response)
}

export async function upsertWebhook(options: UpsertWebhookOptions): Promise<ComponentWebhookRegistrationRecord> {
    const sourcePath = encodeSourcePathForRoute(validateComponentEntrypoint(options.sourcePath))
    const response = await requestJson(
        `/webhooks/${sourcePath}`,
        {
            method: 'PUT',
            body: JSON.stringify({
                workspaceBlobId: validateRequiredString(options.workspaceBlobId, 'workspaceBlobId'),
                callbackUrl: normalizeWebhookCallbackUrl(options.callbackUrl),
                secret: validateRequiredString(options.secret, 'secret').trim(),
                events: normalizeWebhookEvents(options.events),
                runnerBootstrap: normalizeComponentRunnerBootstrap(options.runnerBootstrap),
            }),
        },
        options,
    )

    return parseWebhookRegistrationRecord(response)
}

export async function getWebhooks(options: GetWebhooksOptions): Promise<ComponentWebhookRegistrationRecord[]> {
    const workspaceBlobId = validateRequiredString(options.workspaceBlobId, 'workspaceBlobId')
    const response = await requestJson(`/webhooks?workspaceBlobId=${encodeURIComponent(workspaceBlobId)}`, {}, options)
    return parseWebhookRegistrationRecordList(response)
}

export async function deleteWebhook(options: DeleteWebhookOptions): Promise<ComponentWebhookRegistrationRecord> {
    const webhookId = validateRouteName(options.webhookId, 'webhookId')
    const response = await requestJson(`/webhooks/${encodeURIComponent(webhookId)}`, { method: 'DELETE' }, options)
    return parseWebhookRegistrationRecord(response)
}

export async function getWebhookDeliveries(
    options: GetWebhookDeliveriesOptions,
): Promise<ComponentWebhookWithDeliveriesRecord> {
    const webhookId = validateRouteName(options.webhookId, 'webhookId')
    const response = await requestJson(
        `/webhooks/${encodeURIComponent(webhookId)}/deliveries`,
        {},
        options,
    )
    return parseWebhookWithDeliveriesRecord(response)
}

export async function getActiveJobs(options: ComponentJobsClientOptions): Promise<ActiveComponentJobsRecord> {
    const response = await requestJson('/jobs/active', {}, options)
    return parseActiveJobsRecord(response)
}

export async function getWorkspaceJobs(options: GetWorkspaceJobsOptions): Promise<WorkspaceComponentJobsRecord> {
    const workspaceBlobId = validateRequiredString(options.workspaceBlobId, 'workspaceBlobId')
    const query = buildWorkspaceJobsQuery(options)
    const response = await requestJson(`/workspaces/${encodeURIComponent(workspaceBlobId)}/jobs${query}`, {}, options)
    return parseWorkspaceJobsRecord(response)
}

function buildWorkspaceJobsQuery(options: GetWorkspaceJobsOptions): string {
    const params = new URLSearchParams()
    if (typeof options.sourcePath === 'string' && options.sourcePath.trim().length > 0) {
        params.set('sourcePath', validateComponentEntrypoint(options.sourcePath))
    }
    if (typeof options.limit === 'number') {
        if (!Number.isFinite(options.limit) || options.limit <= 0) {
            throw new SdkError('INVALID_REQUEST', 'limit must be a positive number')
        }
        params.set('limit', String(Math.floor(options.limit)))
    }
    const query = params.toString()
    return query.length > 0 ? `?${query}` : ''
}

export async function getAppIacStatus(options: GetAppIacStatusOptions): Promise<SecureBlobAppIacStatusResult> {
    const workspaceRefs = Array.from(new Set(options.workspaceRefs
        .map(ref => validateRequiredString(ref, 'workspaceRefs[]').trim())
        .filter(ref => ref.length > 0)))

    const response = await requestJson(
        '/app-iac/status',
        {
            method: 'POST',
            body: JSON.stringify({
                configConnectionToken: validateRequiredString(options.configConnectionToken, 'configConnectionToken'),
                workspaceRefs,
                appManifestPath: options.appManifestPath,
                mailboxRoot: options.mailboxRoot,
            }),
        },
        options,
    )

    return parseAppIacStatusResult(response)
}

function validateComponentEntrypoint(path: string): string {
    const normalized = validatePath(path)
    if (normalized === '/') {
        throw new SdkError('INVALID_PATH', 'Component entrypoint must not be the workspace root')
    }
    if (!COMPONENT_JOB_SOURCE_EXTENSIONS.some(extension => normalized.toLowerCase().endsWith(extension))) {
        throw new SdkError('INVALID_PATH', `Component entrypoint must be a JS/TS file: "${path}"`)
    }
    return normalized
}

function validateBuildArtifactRoot(path: string): string {
    const normalized = validatePath(path)
    if (normalized === '/') {
        throw new SdkError('INVALID_PATH', 'Artifact root must not be the workspace root')
    }
    return normalized
}

function validateRequiredString(value: string, fieldName: string): string {
    if (typeof value !== 'string' || value.trim().length === 0) {
        throw new SdkError('INVALID_PATH', `${fieldName} must be a non-empty string`)
    }
    return value
}

function validateRouteName(value: string, fieldName: string): string {
    const normalized = validateRequiredString(value, fieldName)
    validateName(normalized)
    return normalized
}

function normalizeToolchain(toolchain: ComponentArtifactToolchain): ComponentArtifactToolchain {
    const normalizedEntries = Object.entries(toolchain)
        .map(([key, value]) => [validateRequiredString(key, 'toolchain key'), validateRequiredString(value, `toolchain.${key}`)] as const)
        .sort(([left], [right]) => left.localeCompare(right))

    if (normalizedEntries.length === 0) {
        throw new SdkError('INVALID_PATH', 'toolchain must include at least one version entry')
    }

    return Object.fromEntries(normalizedEntries) as ComponentArtifactToolchain
}

function normalizeWebhookEvents(events: readonly string[]): ComponentWebhookEvent[] {
    if (!Array.isArray(events) || events.length === 0) {
        throw new SdkError('INVALID_PATH', 'events must include at least one webhook event')
    }

    const normalized = events
        .map((event, index) => validateRequiredString(event, `events[${index}]`).trim())
        .filter((event, index, all) => all.indexOf(event) === index)
        .sort((left, right) => left.localeCompare(right))

    if (normalized.length === 0) {
        throw new SdkError('INVALID_PATH', 'events must include at least one webhook event')
    }

    return normalized.map(event => expectAllowedString(event, COMPONENT_WEBHOOK_EVENTS, 'webhook event'))
}

function normalizeWebhookCallbackUrl(callbackUrl: string): string {
    const normalized = validateRequiredString(callbackUrl, 'callbackUrl').trim()
    let url: URL
    try {
        url = new URL(normalized)
    } catch {
        throw new SdkError('INVALID_PATH', 'callbackUrl must be an absolute http/https URL')
    }

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        throw new SdkError('INVALID_PATH', 'callbackUrl must be an absolute http/https URL')
    }

    return url.toString()
}

function normalizeHandlerTimeoutSeconds(timeoutSeconds: number | undefined): number {
    if (typeof timeoutSeconds !== 'number' || !Number.isFinite(timeoutSeconds)) {
        return 30
    }

    return Math.min(120, Math.max(1, Math.round(timeoutSeconds)))
}

function normalizeComponentBuildTarget(
    entrypoints: readonly string[],
    buildTarget: ComponentBuildTarget | undefined,
): ComponentBuildTarget {
    const derived = deriveComponentBuildTarget(entrypoints)
    if (!buildTarget) {
        return derived
    }

    const kind = isAllowedValue(buildTarget.kind, COMPONENT_BUILD_TARGET_KINDS)
        ? buildTarget.kind
        : derived.kind
    const declaredPath = validatePath(buildTarget.declaredPath)

    return {
        kind,
        declaredPath,
    }
}

function normalizeGuestSdkInfo(guestSdk: ComponentGuestSdkInfo | undefined): ComponentGuestSdkInfo {
    return {
        name: validateRequiredString(guestSdk?.name ?? '@secureblob/sdk', 'guestSdk.name'),
        version: validateRequiredString(guestSdk?.version ?? '0.0.0-unknown', 'guestSdk.version'),
        runtimePackage: validateRequiredString(guestSdk?.runtimePackage ?? '@secureblob/component-sdk', 'guestSdk.runtimePackage'),
        runtimeVersion: validateRequiredString(guestSdk?.runtimeVersion ?? '0.0.0-unknown', 'guestSdk.runtimeVersion'),
    }
}

function normalizeHostCapabilities(capabilities: ComponentHostCapabilities | undefined): ComponentHostCapabilities {
    return {
        configTransfer: capabilities?.configTransfer ?? true,
        fetch: capabilities?.fetch ?? true,
        streams: capabilities?.streams ?? false,
        sqlite: capabilities?.sqlite ?? false,
        stdout: capabilities?.stdout ?? true,
        stderr: capabilities?.stderr ?? true,
        events: capabilities?.events ?? true,
    }
}

function normalizeSdkSurfaces(surfaces: ComponentSdkSurfaces | undefined): ComponentSdkSurfaces {
    return {
        workspaceFs: surfaces?.workspaceFs ?? true,
        mcp: surfaces?.mcp ?? false,
        agent: surfaces?.agent ?? false,
        shimmedFetch: surfaces?.shimmedFetch ?? true,
    }
}

function normalizeRunnerCompatibility(
    compatibility: ComponentRunnerCompatibility | undefined,
): ComponentRunnerCompatibility {
    if (!compatibility) {
        return {
            wasmtime: {
                supported: true,
                required: true,
                asyncModel: 'wasmtime-cli-component-v1',
            },
        }
    }

    return {
        wasmtime: compatibility.wasmtime
            ? normalizeRunnerEngineCompatibility(compatibility.wasmtime, 'runnerCompatibility.wasmtime')
            : {
                supported: false,
                required: false,
                reason: 'wasmtime compatibility was not declared by this artifact.',
            },
        ...(compatibility.node
            ? { node: normalizeRunnerEngineCompatibility(compatibility.node, 'runnerCompatibility.node') }
            : {}),
    }
}

function normalizeRunnerEngineCompatibility(
    engine: ComponentRunnerEngineCompatibility,
    fieldName: string,
): ComponentRunnerEngineCompatibility {
    return {
        supported: engine.supported === true,
        required: engine.required === true,
        ...(engine.asyncModel ? { asyncModel: validateRequiredString(engine.asyncModel, `${fieldName}.asyncModel`) } : {}),
        ...(engine.reason ? { reason: validateRequiredString(engine.reason, `${fieldName}.reason`) } : {}),
        ...(engine.localStorePolicy
            ? { localStorePolicy: engine.localStorePolicy === 'indexeddb' ? 'indexeddb' : 'memory' }
            : {}),
    }
}

function normalizeRuntimeProfile(
    runtimeProfile: Readonly<Record<string, ComponentRuntimeProfileValue>>,
): Record<string, ComponentRuntimeProfileValue> {
    const normalizedEntries = Object.entries(runtimeProfile)
        .map(([key, value]) => {
            if (typeof value === 'undefined') {
                throw new SdkError('INVALID_PATH', `runtimeProfile.${key} must not be undefined`)
            }
            return [validateRequiredString(key, 'runtimeProfile key'), value] as const
        })
        .sort(([left], [right]) => left.localeCompare(right))

    if (normalizedEntries.length === 0) {
        throw new SdkError('INVALID_PATH', 'runtimeProfile must include at least one flag')
    }

    return Object.fromEntries(normalizedEntries)
}

function normalizeComponentRunnerBootstrap(input: ComponentRunnerBootstrap): ComponentRunnerBootstrap {
    const apiBaseUrl = validateRequiredString(input.apiBaseUrl, 'runnerBootstrap.apiBaseUrl')
    const workspaceBlobId = normalizeOptionalString(input.workspaceBlobId)
    const workspaceAlias = normalizeOptionalString(input.workspaceAlias)
    const workspaceSecret = normalizeOptionalString(input.workspaceSecret)
    const configConnectionToken = normalizeOptionalString(input.configConnectionToken)
    const localStorePolicy = input.localStorePolicy === 'indexeddb' ? 'indexeddb' : 'memory'
    const secretEnv = normalizeSecretEnv(input.secretEnv)

    if (!workspaceSecret && !configConnectionToken) {
        throw new SdkError(
            'INVALID_PATH',
            'Component runner bootstrap requires either workspaceSecret or configConnectionToken',
        )
    }

    return {
        apiBaseUrl,
        ...(workspaceBlobId ? { workspaceBlobId } : {}),
        ...(workspaceAlias ? { workspaceAlias } : {}),
        ...(workspaceSecret ? { workspaceSecret } : {}),
        ...(configConnectionToken ? { configConnectionToken } : {}),
        localStorePolicy,
        ...(Object.keys(secretEnv).length > 0 ? { secretEnv } : {}),
    }
}

function normalizeSecretEnv(input: Record<string, string> | null | undefined): Record<string, string> {
    if (!input || typeof input !== 'object') {
        return {}
    }

    const entries = Object.entries(input)
        .map(([name, secretRef]) => [
            validateRequiredString(name, 'runnerBootstrap.secretEnv name'),
            validateRequiredString(secretRef, `runnerBootstrap.secretEnv.${name}`),
        ] as const)
        .sort(([left], [right]) => left.localeCompare(right))

    return Object.fromEntries(entries)
}

function normalizeRequiredComponentRunnerBootstrap(
    input: ComponentRunnerBootstrap | null | undefined,
    fieldName: string,
): ComponentRunnerBootstrap {
    if (!input) {
        throw new SdkError('INVALID_PATH', `${fieldName} is required`)
    }

    return normalizeComponentRunnerBootstrap(input)
}

function normalizeArtifactFiles(files: readonly ComponentArtifactFileEntry[]): ComponentArtifactFileEntry[] {
    return files
        .map(file => ({
            path: validateRelativeArtifactPath(file.path),
            ...(file.sha256 ? { sha256: validateRequiredString(file.sha256, `files[${file.path}].sha256`) } : {}),
            ...(typeof file.size === 'number' ? { size: validateArtifactFileSize(file.size, file.path) } : {}),
        }))
        .sort((left, right) => left.path.localeCompare(right.path))
}

function validateRelativeArtifactPath(path: string): string {
    const normalized = path.replace(/\\/g, '/').replace(/\/+/g, '/')
    if (!normalized || normalized.startsWith('/') || normalized.endsWith('/')) {
        throw new SdkError('INVALID_PATH', `Artifact file path must be relative: "${path}"`)
    }

    for (const segment of normalized.split('/')) {
        validateName(segment)
    }

    return normalized
}

function validateArtifactFileSize(size: number, path: string): number {
    if (!Number.isFinite(size) || size < 0) {
        throw new SdkError('INVALID_PATH', `Artifact file size must be a non-negative number for "${path}"`)
    }
    return size
}

function validateRunLogName(logName: string): string {
    switch (normalizeOptionalString(logName).toLowerCase()) {
        case 'stdout.log':
            return 'stdout.log'
        case 'stderr.log':
            return 'stderr.log'
        case 'events.jsonl':
            return 'events.jsonl'
        case 'http.jsonl':
            return 'http.jsonl'
        case 'mcp.jsonl':
            return 'mcp.jsonl'
        case 'status-cards.jsonl':
            return 'status-cards.jsonl'
        default:
            throw new SdkError('INVALID_PATH', `Unsupported component run log name: "${logName}"`)
    }
}

function normalizeArtifactIdentityInput(input: ComponentArtifactIdentityInput) {
    return {
        sourceSnapshot: validateRequiredString(input.sourceSnapshot, 'sourceSnapshot'),
        entrypoints: normalizeComponentEntrypoints(input.entrypoints),
        dependencySnapshot: validateRequiredString(input.dependencySnapshot, 'dependencySnapshot'),
        worldVersion: validateRequiredString(input.worldVersion, 'worldVersion'),
        toolchain: normalizeToolchain(input.toolchain),
        runtimeProfile: normalizeRuntimeProfile(input.runtimeProfile),
    }
}

function deriveEntrypointCommonDirectory(entrypoints: readonly string[]): string {
    const directorySegments = entrypoints.map(entrypoint =>
        entrypoint
            .split('/')
            .slice(0, -1)
            .filter(Boolean),
    )
    const first = directorySegments[0] ?? []
    const shared: string[] = []

    for (let index = 0; index < first.length; index += 1) {
        const segment = first[index]
        if (directorySegments.every(parts => parts[index] === segment)) {
            shared.push(segment)
            continue
        }
        break
    }

    return shared.length === 0 ? '/' : `/${shared.join('/')}`
}

function matchesAnyImport(imports: string[], patterns: RegExp[]): boolean {
    return imports.some(specifier => patterns.some(pattern => pattern.test(specifier)))
}

function normalizeComponentJobTimezone(value: string | undefined): string {
    const normalized = normalizeOptionalString(value)
    return normalized || DEFAULT_COMPONENT_JOB_TIMEZONE
}

function deriveStarterServiceName(sourcePath: string): string {
    const folderFirstRoot = getFolderFirstComponentJobRootFromEntrypoint(sourcePath)
    if (folderFirstRoot) {
        const folderName = folderFirstRoot.split('/').pop() ?? 'container-service'
        const normalizedFolderName = folderName.replace(/\.job$/i, '')
        const normalizedStem = normalizedFolderName
            .replace(/[^A-Za-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .toLowerCase()
        if (normalizedStem.length > 0) {
            return normalizedStem
        }
    }

    const fileName = sourcePath.split('/').pop() ?? 'container-service'
    const stem = fileName.replace(/\.[^.]+$/, '')
    const normalizedStem = stem
        .replace(/[^A-Za-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .toLowerCase()

    return normalizedStem.length > 0 ? normalizedStem : 'container-service'
}

function normalizeOptionalString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : ''
}

function readResponseHeader(response: Response, name: string): string | undefined {
    const value = response.headers?.get(name)
    return value && value.trim().length > 0
        ? value.trim()
        : undefined
}

function normalizeComponentJobServerRunner(
    value: unknown,
): ComponentJobServerRunner | undefined {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return undefined
    }

    const label = normalizeOptionalString((value as { label?: unknown }).label)
    const configBlobId = normalizeOptionalString((value as { configBlobId?: unknown }).configBlobId)
    const connectionCredential = normalizeOptionalString((value as { connectionCredential?: unknown }).connectionCredential)
    if (!label || !configBlobId || !connectionCredential) {
        return undefined
    }

    return {
        purpose: 'server-runner',
        label,
        configBlobId,
        connectionCredential,
    }
}

function normalizeComponentJobEntry(value: unknown): string | undefined {
    const normalized = normalizeOptionalString(value).replace(/\\/g, '/')
    if (!normalized) {
        return undefined
    }
    if (normalized.startsWith('/')) {
        return undefined
    }

    const relativePath = normalized.startsWith('./') ? normalized.slice(2) : normalized
    if (!relativePath || relativePath.startsWith('../') || relativePath.includes('/../')) {
        return undefined
    }

    try {
        validateComponentEntrypoint(`/${relativePath}`)
    } catch {
        return undefined
    }

    return `./${relativePath}`
}

function isAllowedValue<T extends readonly string[]>(
    value: unknown,
    allowedValues: T,
): value is T[number] {
    return typeof value === 'string' && allowedValues.includes(value)
}

function deriveSourcePathFromComponentCompanion(path: string): string | null {
    if (path.endsWith('.job.json')) {
        return path.slice(0, -'.job.json'.length)
    }
    const folderFirstRoot = getComponentJobRootPath(path)
    if (folderFirstRoot && path !== folderFirstRoot) {
        return validateComponentEntrypoint(`${folderFirstRoot}/main.tsx`)
    }
    if (path.endsWith('.runs')) {
        return path.slice(0, -'.runs'.length)
    }
    const runsIndex = path.indexOf('.runs/')
    if (runsIndex >= 0) {
        return path.slice(0, runsIndex)
    }
    if (path.endsWith('.builds')) {
        return path.slice(0, -'.builds'.length)
    }
    const buildsIndex = path.indexOf('.builds/')
    if (buildsIndex >= 0) {
        return path.slice(0, buildsIndex)
    }
    return null
}

export interface ComponentJobConversionPlan {
    sourcePath: string
    jobRootPath: string
    newSourcePath: string
    newSettingsPath: string
    newRunsDir: string
    newBuildsDir: string
}

export function deriveComponentJobConversionPlan(sourcePath: string): ComponentJobConversionPlan {
    const normalized = validateComponentEntrypoint(sourcePath)

    if (getFolderFirstComponentJobRootFromEntrypoint(normalized)) {
        throw new SdkError('INVALID_PATH', 'Source path is already inside a folder-first job root')
    }

    const extension = normalized.substring(normalized.lastIndexOf('.'))
    const stem = normalized.substring(0, normalized.length - extension.length)
    const jobRootPath = `${stem}.job`
    const sourceFileName = normalized.split('/').pop()!
    const entryExtension = sourceFileName.substring(sourceFileName.lastIndexOf('.'))

    return {
        sourcePath: normalized,
        jobRootPath,
        newSourcePath: validatePath(`${jobRootPath}/main${entryExtension}`),
        newSettingsPath: validatePath(`${jobRootPath}/job.json`),
        newRunsDir: validatePath(`${jobRootPath}/.secureblob/runs`),
        newBuildsDir: validatePath(`${jobRootPath}/.secureblob/artifacts`),
    }
}

function getFolderFirstComponentJobRootFromEntrypoint(path: string): string | null {
    const normalized = validatePath(path)
    const fileName = normalized.split('/').pop()
    const parentPath = normalized.slice(0, Math.max(0, normalized.lastIndexOf('/')))
    if (!fileName || !parentPath.endsWith('.job')) {
        return null
    }
    // Accept any "main.*" with a valid JS/TS extension (not just main.tsx)
    const isMainWithValidExtension = COMPONENT_JOB_SOURCE_EXTENSIONS.some(
        ext => fileName === `main${ext}`,
    )
    if (!isMainWithValidExtension) {
        return null
    }
    return parentPath || null
}

function parseRunPath(path: string, sourcePath: string, runsDir: string): ParsedComponentJobPath {
    const relativePath = path.slice(runsDir.length + 1)
    const [runId, ...rest] = relativePath.split('/')
    validateName(runId)
    const remainder = rest.join('/')

    if (!remainder) {
        return { path, sourcePath, kind: 'run_dir', runId }
    }
    if (remainder === 'run.json') {
        return { path, sourcePath, kind: 'run_metadata', runId }
    }
    if (remainder === 'stdout.log') {
        return { path, sourcePath, kind: 'run_stdout', runId }
    }
    if (remainder === 'stderr.log') {
        return { path, sourcePath, kind: 'run_stderr', runId }
    }
    if (remainder === 'events.jsonl') {
        return { path, sourcePath, kind: 'run_events', runId }
    }
    if (remainder === 'http.jsonl') {
        return { path, sourcePath, kind: 'run_http', runId }
    }
    if (remainder === 'mcp.jsonl') {
        return { path, sourcePath, kind: 'run_mcp', runId }
    }
    if (remainder === 'status-cards.jsonl') {
        return { path, sourcePath, kind: 'run_status_cards', runId }
    }
    if (remainder === 'outputs') {
        return { path, sourcePath, kind: 'run_outputs_dir', runId }
    }
    if (remainder.startsWith('outputs/')) {
        return {
            path,
            sourcePath,
            kind: 'run_output',
            runId,
            relativePath: remainder.slice('outputs/'.length),
        }
    }

    return { path, sourcePath, kind: 'run_file', runId, relativePath: remainder }
}

function parseBuildPath(path: string, sourcePath: string, buildsDir: string): ParsedComponentJobPath {
    const relativePath = path.slice(buildsDir.length + 1)
    const [rootSegment, ...rest] = relativePath.split('/')
    validateName(rootSegment)

    if (rootSegment === '_attempts') {
        return parseBuildAttemptPath(path, sourcePath, rest.join('/'))
    }

    const artifactId = rootSegment
    const remainder = rest.join('/')

    if (!remainder) {
        return { path, sourcePath, kind: 'artifact_dir', artifactId }
    }
    if (remainder === 'manifest.json') {
        return { path, sourcePath, kind: 'artifact_manifest', artifactId }
    }
    if (remainder === 'component.wasm') {
        return { path, sourcePath, kind: 'artifact_component_wasm', artifactId }
    }
    if (remainder === 'build-log.txt') {
        return { path, sourcePath, kind: 'artifact_build_log', artifactId }
    }

    return { path, sourcePath, kind: 'artifact_file', artifactId, relativePath: remainder }
}

function parseBuildAttemptPath(path: string, sourcePath: string, relativePath: string): ParsedComponentJobPath {
    if (!relativePath) {
        return { path, sourcePath, kind: 'build_attempts_dir' }
    }

    const [buildId, ...rest] = relativePath.split('/')
    validateName(buildId)
    const remainder = rest.join('/')

    if (!remainder) {
        return { path, sourcePath, kind: 'build_attempt_dir', buildId }
    }
    if (remainder === 'build.json') {
        return { path, sourcePath, kind: 'build_attempt_metadata', buildId }
    }
    if (remainder === 'build-log.txt') {
        return { path, sourcePath, kind: 'build_attempt_build_log', buildId }
    }
    if (remainder === 'outputs') {
        return { path, sourcePath, kind: 'build_attempt_outputs_dir', buildId }
    }
    if (remainder.startsWith('outputs/')) {
        return {
            path,
            sourcePath,
            kind: 'build_attempt_output',
            buildId,
            relativePath: remainder.slice('outputs/'.length),
        }
    }

    return { path, sourcePath, kind: 'build_attempt_file', buildId, relativePath: remainder }
}

function encodeSourcePathForRoute(sourcePath: string): string {
    return sourcePath
        .slice(1)
        .split('/')
        .map(segment => encodeURIComponent(validateRouteName(segment, 'sourcePath segment')))
        .join('/')
}

function encodeArtifactFilePath(path: string): string {
    return path
        .split('/')
        .map(segment => encodeURIComponent(validateRouteName(segment, 'artifact file segment')))
        .join('/')
}

function resolveFetchImpl(options: ComponentJobsClientOptions): ComponentJobsFetch {
    if (typeof options.fetch === 'function') {
        return options.fetch
    }
    if (typeof globalThis.fetch === 'function') {
        return globalThis.fetch.bind(globalThis)
    }
    throw new SdkError('SDK_NOT_INITIALIZED', 'Component jobs client requires a fetch implementation')
}

function normalizeComponentJobsApiBaseUrl(apiBaseUrl: string): string {
    return validateRequiredString(apiBaseUrl, 'apiBaseUrl').replace(/\/+$/, '')
}

function mergeRequestHeaders(initHeaders: HeadersInit | undefined, extraHeaders: HeadersInit | undefined): Headers {
    const headers = new Headers(initHeaders)
    if (extraHeaders) {
        const extra = new Headers(extraHeaders)
        extra.forEach((value, key) => {
            headers.set(key, value)
        })
    }
    return headers
}

async function createRequestHeaders(
    initHeaders: HeadersInit | undefined,
    options: ComponentJobsClientOptions,
): Promise<Headers> {
    const headers = mergeRequestHeaders(initHeaders, options.headers)
    if (options.authProvider) {
        const scope = options.authScope ?? 'write'
        const accessToken = await options.authProvider.getAccessToken(scope, options.signal)
        headers.set('authorization', `Bearer ${accessToken.token}`)
    }
    return headers
}

async function requestJson(
    path: string,
    init: RequestInit,
    options: ComponentJobsClientOptions,
): Promise<unknown> {
    const response = await performRequest(path, init, options)
    const payload = await readJsonPayload(response)

    if (!response.ok) {
        throw createHttpError(response, payload)
    }

    return payload
}

async function requestBytes(
    path: string,
    init: RequestInit,
    options: ComponentJobsClientOptions,
): Promise<Uint8Array> {
    const response = await performRequest(path, init, options)

    if (!response.ok) {
        throw createHttpError(response, await readJsonPayload(response))
    }

    return new Uint8Array(await response.arrayBuffer())
}

async function requestText(
    path: string,
    init: RequestInit,
    options: ComponentJobsClientOptions,
): Promise<string> {
    const response = await performRequest(path, init, options)

    if (!response.ok) {
        throw createHttpError(response, await readJsonPayload(response))
    }

    return await response.text()
}

async function performRequest(
    path: string,
    init: RequestInit,
    options: ComponentJobsClientOptions,
): Promise<Response> {
    const fetchImpl = resolveFetchImpl(options)
    const url = `${normalizeComponentJobsApiBaseUrl(options.apiBaseUrl)}${path}`
    const headers = await createRequestHeaders(init.headers, options)

    if (init.body !== undefined && !headers.has('content-type')) {
        headers.set('content-type', 'application/json')
    }

    try {
        const response = await fetchImpl(url, {
            ...init,
            headers,
            signal: init.signal ?? options.signal,
        })
        if (response.status !== 401 || !options.authProvider) {
            return response
        }

        const scope = options.authScope ?? 'write'
        options.authProvider.invalidateToken(scope)
        const retryHeaders = await createRequestHeaders(init.headers, options)
        if (init.body !== undefined && !retryHeaders.has('content-type')) {
            retryHeaders.set('content-type', 'application/json')
        }
        return await fetchImpl(url, {
            ...init,
            headers: retryHeaders,
            signal: init.signal ?? options.signal,
        })
    } catch (error) {
        if (error instanceof SdkError) {
            throw error
        }
        if (error instanceof Error && error.name === 'AbortError') {
            throw new SdkError('ABORTED', 'Component jobs request was aborted', { cause: error, retryable: false })
        }
        throw new SdkError('NETWORK_ERROR', `Component jobs request failed: ${error instanceof Error ? error.message : String(error)}`, {
            cause: error,
            retryable: true,
        })
    }
}

async function readJsonPayload(response: Response): Promise<unknown> {
    const text = await response.text()
    if (text.trim().length === 0) {
        return null
    }

    try {
        return JSON.parse(text) as unknown
    } catch (error) {
        throw new SdkError('CORRUPT_DATA', `Component jobs API returned invalid JSON (${response.status})`, {
            cause: error,
            retryable: false,
        })
    }
}

function createHttpError(response: Response, payload: unknown): SdkError {
    const message = getHttpErrorMessage(response, payload)
    const code = response.status === 400
        ? 'INVALID_REQUEST'
        : response.status === 403
            ? 'FORBIDDEN'
            : response.status === 404
                ? 'NOT_FOUND'
                : 'NETWORK_ERROR'

    return new SdkError(code, message, {
        cause: payload,
        retryable: code === 'NETWORK_ERROR' && response.status >= 500,
    })
}

function getHttpErrorMessage(response: Response, payload: unknown): string {
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
        const message = (payload as Record<string, unknown>).message
        if (typeof message === 'string' && message.trim().length > 0) {
            return message
        }

        const error = (payload as Record<string, unknown>).error
        if (typeof error === 'string' && error.trim().length > 0) {
            return error
        }
    }

    return `Component jobs API request failed with ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`
}

function extractHandlerRunId(payload: unknown): string | undefined {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return undefined
    }

    const runId = (payload as Record<string, unknown>).runId
    return typeof runId === 'string' && runId.trim().length > 0
        ? runId.trim()
        : undefined
}

function extractHandlerErrorCode(payload: unknown): string | undefined {
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return undefined
    }

    const error = (payload as Record<string, unknown>).error
    return typeof error === 'string' && error.trim().length > 0
        ? error.trim()
        : undefined
}

function parseBuildStatusRecord(value: unknown): ComponentBuildStatusRecord {
    const record = expectRecord(value, 'build status')
    return {
        buildId: expectString(record.buildId, 'buildId'),
        sourceSnapshot: expectString(record.sourceSnapshot, 'sourceSnapshot'),
        entrypoints: expectStringArray(record.entrypoints, 'entrypoints'),
        workspaceBlobId: expectString(record.workspaceBlobId, 'workspaceBlobId'),
        stage: expectAllowedString(record.stage, COMPONENT_BUILD_STAGES, 'stage'),
        artifactId: expectOptionalString(record.artifactId, 'artifactId'),
        error: expectOptionalString(record.error, 'error'),
        message: expectString(record.message, 'message'),
        startedAt: expectString(record.startedAt, 'startedAt'),
        stageStartedAt: expectString(record.stageStartedAt, 'stageStartedAt'),
        totalElapsedSeconds: expectNumber(record.totalElapsedSeconds, 'totalElapsedSeconds'),
        stageElapsedSeconds: expectNumber(record.stageElapsedSeconds, 'stageElapsedSeconds'),
        updatedAt: expectString(record.updatedAt, 'updatedAt'),
    }
}

function parseRunStatusRecord(value: unknown): ComponentRunStatusRecord {
    const record = expectRecord(value, 'run status')
    return {
        runId: expectString(record.runId, 'runId'),
        buildId: expectOptionalString(record.buildId, 'buildId'),
        artifactId: expectOptionalString(record.artifactId, 'artifactId'),
        workspaceBlobId: expectString(record.workspaceBlobId, 'workspaceBlobId'),
        sourcePath: expectOptionalString(record.sourcePath, 'sourcePath'),
        engine: expectOptionalAllowedString(record.engine, COMPONENT_SERVER_ENGINES, 'engine'),
        stage: expectAllowedString(record.stage, COMPONENT_RUN_STAGES, 'stage'),
        error: expectOptionalString(record.error, 'error'),
        message: expectString(record.message, 'message'),
        startedAt: expectString(record.startedAt, 'startedAt'),
        stageStartedAt: expectString(record.stageStartedAt, 'stageStartedAt'),
        totalElapsedSeconds: expectNumber(record.totalElapsedSeconds, 'totalElapsedSeconds'),
        stageElapsedSeconds: expectNumber(record.stageElapsedSeconds, 'stageElapsedSeconds'),
        updatedAt: expectString(record.updatedAt, 'updatedAt'),
    }
}

function parseTriggerDispatchRecord(value: unknown): ComponentTriggerDispatchRecord {
    const record = expectRecord(value, 'trigger dispatch')
    return {
        buildId: expectString(record.buildId, 'buildId'),
        artifactId: expectOptionalString(record.artifactId, 'artifactId'),
        runId: expectString(record.runId, 'runId'),
        workspaceBlobId: expectString(record.workspaceBlobId, 'workspaceBlobId'),
        sourcePath: expectString(record.sourcePath, 'sourcePath'),
        status: expectAllowedString(record.status, ['queued'] as const, 'status'),
    }
}

function parseWebhookRegistrationRecord(value: unknown): ComponentWebhookRegistrationRecord {
    const record = expectRecord(value, 'webhook registration')
    return {
        webhookId: expectString(record.webhookId, 'webhookId'),
        workspaceBlobId: expectString(record.workspaceBlobId, 'workspaceBlobId'),
        sourcePath: expectString(record.sourcePath, 'sourcePath'),
        callbackUrl: expectString(record.callbackUrl, 'callbackUrl'),
        events: expectArray(record.events, 'events').map((item, index) =>
            expectAllowedString(item, COMPONENT_WEBHOOK_EVENTS, `events[${index}]`),
        ),
        hasRunnerBootstrap: expectBoolean(record.hasRunnerBootstrap, 'hasRunnerBootstrap'),
        createdAt: expectString(record.createdAt, 'createdAt'),
        updatedAt: expectString(record.updatedAt, 'updatedAt'),
    }
}

function parseScheduleRecord(value: unknown): ComponentJobScheduleRecord {
    const record = expectRecord(value, 'schedule')
    return {
        scheduleId: expectString(record.scheduleId, 'scheduleId'),
        artifactId: expectString(record.artifactId, 'artifactId'),
        workspaceBlobId: expectString(record.workspaceBlobId, 'workspaceBlobId'),
        sourcePath: expectString(record.sourcePath, 'sourcePath'),
        enabled: expectBoolean(record.enabled, 'enabled'),
        schedulePreset: expectAllowedString(record.schedulePreset, COMPONENT_JOB_SCHEDULE_PRESETS, 'schedulePreset'),
        cron: expectString(record.cron, 'cron'),
        timezone: expectString(record.timezone, 'timezone'),
        enginePolicy: expectAllowedString(record.enginePolicy, COMPONENT_JOB_ENGINE_POLICIES, 'enginePolicy'),
        concurrencyPolicy: expectAllowedString(record.concurrencyPolicy, COMPONENT_JOB_CONCURRENCY_POLICIES, 'concurrencyPolicy'),
        payloadPreset: expectOptionalString(record.payloadPreset, 'payloadPreset'),
        jobRoot: expectOptionalString(record.jobRoot, 'jobRoot'),
        successors: parseOptionalSuccessorList(record.successors),
        createdAt: expectString(record.createdAt, 'createdAt'),
        updatedAt: expectString(record.updatedAt, 'updatedAt'),
    }
}

function parseOptionalSuccessorList(value: unknown): ComponentJobSuccessor[] | undefined {
    if (value === undefined || value === null) {
        return undefined
    }
    const arr = expectArray(value, 'successors')
    return arr.map((item, index) => {
        const record = expectRecord(item, `successor[${index}]`)
        return {
            sourcePath: expectString(record.sourcePath, `successor[${index}].sourcePath`),
        }
    })
}

function parseWebhookRegistrationRecordList(value: unknown): ComponentWebhookRegistrationRecord[] {
    return expectArray(value, 'webhooks').map(item => parseWebhookRegistrationRecord(item))
}

const COMPONENT_WEBHOOK_DELIVERY_STATUSES: readonly ComponentWebhookDeliveryStatus[] = [
    'pending',
    'delivered',
    'failed',
    'retry_scheduled',
    'dead',
]

const COMPONENT_WEBHOOK_DELIVERY_ATTEMPT_STATUSES: readonly ComponentWebhookDeliveryAttemptStatus[] = [
    'delivered',
    'failed',
    'cancelled',
]

function parseWebhookDeliveryAttempt(value: unknown): ComponentWebhookDeliveryAttempt {
    const record = expectRecord(value, 'webhook delivery attempt')
    return {
        attemptNumber: expectNumber(record.attemptNumber, 'attemptNumber'),
        attemptedAt: expectString(record.attemptedAt, 'attemptedAt'),
        httpStatusCode: record.httpStatusCode === null ? null : expectNumber(record.httpStatusCode, 'httpStatusCode'),
        status: expectAllowedString(record.status, COMPONENT_WEBHOOK_DELIVERY_ATTEMPT_STATUSES, 'status'),
        error: record.error === null ? null : expectString(record.error, 'error'),
    }
}

function parseWebhookDeliveryRecord(value: unknown): ComponentWebhookDeliveryRecord {
    const record = expectRecord(value, 'webhook delivery')
    return {
        deliveryId: expectString(record.deliveryId, 'deliveryId'),
        webhookId: expectString(record.webhookId, 'webhookId'),
        runId: expectString(record.runId, 'runId'),
        event: expectString(record.event, 'event'),
        attemptCount: expectNumber(record.attemptCount, 'attemptCount'),
        createdAt: expectString(record.createdAt, 'createdAt'),
        updatedAt: expectString(record.updatedAt, 'updatedAt'),
        lastAttemptAt: record.lastAttemptAt === null ? null : expectString(record.lastAttemptAt, 'lastAttemptAt'),
        nextRetryAt: record.nextRetryAt === null ? null : expectString(record.nextRetryAt, 'nextRetryAt'),
        httpStatusCode: record.httpStatusCode === null ? null : expectNumber(record.httpStatusCode, 'httpStatusCode'),
        status: expectAllowedString(record.status, COMPONENT_WEBHOOK_DELIVERY_STATUSES, 'status'),
        lastError: record.lastError === null ? null : expectString(record.lastError, 'lastError'),
        attempts: expectArray(record.attempts, 'attempts').map((item, index) =>
            parseWebhookDeliveryAttempt(item),
        ),
    }
}

function parseWebhookWithDeliveriesRecord(value: unknown): ComponentWebhookWithDeliveriesRecord {
    const record = expectRecord(value, 'webhook with deliveries')
    return {
        ...parseWebhookRegistrationRecord(record),
        deliveries: expectArray(record.deliveries, 'deliveries').map(item => parseWebhookDeliveryRecord(item)),
    }
}

function parseScheduleRecordList(value: unknown): ComponentJobScheduleRecord[] {
    return expectArray(value, 'schedules').map(item => parseScheduleRecord(item))
}

function parseActiveJobsRecord(value: unknown): ActiveComponentJobsRecord {
    const record = expectRecord(value, 'active jobs')
    return {
        builds: expectArray(record.builds, 'builds').map(item => parseBuildStatusRecord(item)),
        runs: expectArray(record.runs, 'runs').map(item => parseRunStatusRecord(item)),
    }
}

function parseWorkspaceJobsRecord(value: unknown): WorkspaceComponentJobsRecord {
    const record = expectRecord(value, 'workspace jobs')
    return {
        builds: expectArray(record.builds, 'builds').map(item => parseBuildStatusRecord(item)),
        runs: expectArray(record.runs, 'runs').map(item => parseRunStatusRecord(item)),
        schedules: expectArray(record.schedules, 'schedules').map(item => parseScheduleRecord(item)),
    }
}

function parseAppIacStatusResult(value: unknown): SecureBlobAppIacStatusResult {
    const record = expectRecord(value, 'app IaC status')
    expectString(record.configBlobId, 'configBlobId')
    expectString(record.appManifestPath, 'appManifestPath')
    expectString(record.mailboxRoot, 'mailboxRoot')
    expectBoolean(record.healthy, 'healthy')
    expectArray(record.workspaces, 'workspaces')
    expectArray(record.managedRegistrations, 'managedRegistrations')
    expectStringArray(record.warnings, 'warnings')
    expectStringArray(record.errors, 'errors')
    return record as unknown as SecureBlobAppIacStatusResult
}

function expectRecord(value: unknown, label: string): Record<string, unknown> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>
    }
    throw new SdkError('CORRUPT_DATA', `Component jobs API returned an invalid ${label} payload`)
}

function expectArray(value: unknown, label: string): unknown[] {
    if (Array.isArray(value)) {
        return value
    }
    throw new SdkError('CORRUPT_DATA', `Component jobs API returned an invalid ${label} payload`)
}

function expectString(value: unknown, label: string): string {
    if (typeof value === 'string') {
        return value
    }
    throw new SdkError('CORRUPT_DATA', `Component jobs API returned an invalid ${label} value`)
}

function expectOptionalString(value: unknown, label: string): string | undefined {
    if (value === null || typeof value === 'undefined') {
        return undefined
    }
    return expectString(value, label)
}

function expectOptionalAllowedString<const TValue extends string>(
    value: unknown,
    allowed: readonly TValue[],
    label: string,
): TValue | undefined {
    const parsed = expectOptionalString(value, label)
    return typeof parsed === 'undefined' ? undefined : expectAllowedString(parsed, allowed, label)
}

function expectStringArray(value: unknown, label: string): string[] {
    return expectArray(value, label).map((item, index) => expectString(item, `${label}[${index}]`))
}

function expectNumber(value: unknown, label: string): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value
    }
    throw new SdkError('CORRUPT_DATA', `Component jobs API returned an invalid ${label} value`)
}

function expectBoolean(value: unknown, label: string): boolean {
    if (typeof value === 'boolean') {
        return value
    }
    throw new SdkError('CORRUPT_DATA', `Component jobs API returned an invalid ${label} value`)
}

function expectAllowedString<T extends readonly string[]>(value: unknown, allowed: T, label: string): T[number] {
    if (typeof value === 'string' && allowed.includes(value)) {
        return value as T[number]
    }
    throw new SdkError('CORRUPT_DATA', `Component jobs API returned an invalid ${label} value`)
}

// ── Guest runtime workspace bootstrap helpers (§217) ────────────────
export {
    openWorkspaceFromRunnerBootstrap,
} from './componentJobs/guestBootstrap'
export type {
    GuestBootstrapResult,
    OpenWorkspaceFromRunnerBootstrapOptions,
} from './componentJobs/guestBootstrap'

// ── Guest-side MCP helper (§218) ──────────────────────────────────
export {
    createMcpClient,
} from './componentJobs/guestMcp'
export type {
    CreateMcpClientOptions,
    GuestMcpClient,
    GuestMcpTool,
} from './componentJobs/guestMcp'
export {
    createLlmExtractionPipeline,
    LlmExtractionBudgetExceededError,
    LlmExtractionPipelineError,
} from './componentJobs/llmPipeline'
export type {
    CreateLlmExtractionPipelineOptions,
    LlmExtractionCallOptions,
    LlmExtractionPipeline,
    LlmExtractionPipelineResult,
    LlmExtractionPipelineStats,
    LlmExtractionUsage,
} from './componentJobs/llmPipeline'

// ── Runtime-neutral bootstrap resolution (§217) ───────────────────
export {
    COMPONENT_JOB_WAL_SHADOW_SIZE,
    resolveBootstrap,
    shouldAttemptBootstrap,
    isIgnorableBootstrapError,
} from './componentJobs/runtimeBootstrap'
export type {
    ResolvedBootstrap,
    WorkspaceHydrationOptions,
    WorkspaceHydrationResult,
} from './componentJobs/runtimeBootstrap'

// ── Source-compatible starter template export (§228) ─────────────
export type {
    WorkspaceTemplate,
    WorkspaceTemplateMeta,
    WorkspaceTemplateFile,
    WorkspaceTemplateDatabase,
    WorkspaceTemplateDirectory,
    WorkspaceTemplateMediaEntry,
    WorkspaceTemplateMediaContext,
    WorkspaceTemplateMediaProcessor,
} from './workspaceTemplate'
export type { ApplyTemplateOptions, ApplyTemplateResult } from './workspaceTemplate'

/**
 * Source-compatible starter template export (§228 + §215)
 *
 * Converts a `ComponentJobScaffold` for any preset (including `multi-file-service`)
 * into a shared `WorkspaceTemplate` artifact so source-file starter flows can
 * reuse the same materializer path as SDK and MCP tooling, instead of writing
 * scaffold files through direct `writeFile()` calls.
 *
 * With §215 landed, `multi-file-service` is fully supported via its `directories`
 * array (['lib', 'templates']); the old rejection is removed.
 */
export function createSourceCompatibleComponentJobStarterTemplate(
    sourcePath: string,
    options: { template?: ComponentJobStarterTemplate } = {},
): WorkspaceTemplate {
    const template = isAllowedValue(options.template, COMPONENT_JOB_STARTER_TEMPLATES)
        ? options.template
        : 'container-service'

    // Derive the .job root from the source path
    // Logic matches starterScaffolds.ts::deriveVirtualJobRootPath
    const normalizedSourcePath = sourcePath.trim()
    const lastSlash = normalizedSourcePath.lastIndexOf('/')
    const parentPath = lastSlash > 0 ? normalizedSourcePath.slice(0, lastSlash) : ''
    const fileName = lastSlash >= 0 ? normalizedSourcePath.slice(lastSlash + 1) : normalizedSourcePath
    const stem = fileName.replace(/\.[^.]+$/, '') || 'component-job'
    const jobRootPath = `${parentPath}/${stem}.job`

    const scaffold = createComponentJobScaffold(jobRootPath, { template })

    // Map ComponentJobScaffoldFile[] to WorkspaceTemplateFile[]
    // (the two shapes are compatible: both have { path, content })
    const templateFiles: WorkspaceTemplateFile[] = scaffold.files.map(f => ({
        path: `/${scaffold.template}/${f.path}`,
        content: f.content,
    }))

    // Map scaffold directories to WorkspaceTemplateDirectory entries.
    const templateDirectories: WorkspaceTemplateDirectory[] = (scaffold.directories ?? []).map(d => ({
        path: `/${scaffold.template}/${d}`,
    }))

    return {
        meta: {
            name: scaffold.template,
            version: '1.0',
            description: scaffold.description,
            tags: { starter: scaffold.template },
        },
        directories: templateDirectories,
        files: templateFiles,
        databases: [],
    }
}

/**
 * Converts a `ComponentJobScaffold` for any preset (including `multi-file-service`)
 * into a `WorkspaceTemplate` artifact using the full job-root-relative path layout.
 *
 * Unlike `createSourceCompatibleComponentJobStarterTemplate` which derives a virtual
 * job root from a source file path and prefixes paths with the template name,
 * this function accepts a concrete `jobRootPath` and maps scaffold paths as
 * `/{jobRootStem}/{relative}` — suitable for applying the template directly to
 * an existing job root workspace directory via `applyWorkspaceTemplate(...)`.
 *
 * §216 core helper: enables FileBrowser, ComponentJobPanel, and SDK callers to
 * replace their local `mkdir()`/`writeFile()` materialization loops with a shared
 * template artifact that can also flow through §212 MCP tooling.
 */
export function createComponentJobScaffoldTemplate(
    jobRootPath: string,
    options: { template?: ComponentJobStarterTemplate } = {},
): WorkspaceTemplate {
    const normalizedJobRoot = validatePath(jobRootPath)
    if (!normalizedJobRoot.endsWith('.job')) {
        throw new SdkError('INVALID_PATH', 'jobRootPath must end with .job')
    }

    const template = isAllowedValue(options.template, COMPONENT_JOB_STARTER_TEMPLATES)
        ? options.template
        : 'container-service'

    const scaffold = createComponentJobScaffold(normalizedJobRoot, { template })

    // Derive the job-root stem for path construction.
    // e.g. '/jobs/my-job.job/' -> 'jobs/my-job.job'
    const stem = normalizedJobRoot.replace(/^\//, '').replace(/\/$/, '')

    const templateFiles: WorkspaceTemplateFile[] = scaffold.files.map(f => ({
        path: `/${stem}/${f.path}`,
        content: f.content,
    }))

    const templateDirectories: WorkspaceTemplateDirectory[] = (scaffold.directories ?? []).map(d => ({
        path: `/${stem}/${d}/`,
    }))

    return {
        meta: {
            name: scaffold.template,
            version: '1.0',
            description: scaffold.description,
            tags: { starter: scaffold.template },
        },
        directories: templateDirectories,
        files: templateFiles,
        databases: [],
    }
}

// ── applyComponentJobScaffold ───────────────────────────────────────
// §250: composes createComponentJobScaffoldTemplate + applyWorkspaceTemplate
// into a single SDK-callable starter materialization path, replacing the
// internal compose that starterScaffolds.ts already performs.
export interface ApplyComponentJobScaffoldOptions {
    /** A live WorkspaceSession already connected to the workspace. */
    session: import('./workspaceTemplate').WorkspaceTemplateSession
    /** Absolute path to the .job root, e.g. '/jobs/my-job.job/'. */
    jobRootPath: string
    /** Starter preset. Default: 'container-service'. */
    template?: ComponentJobStarterTemplate
    /** ApplyTemplateOptions forwarded to applyWorkspaceTemplate. */
    applyOptions?: import('./workspaceTemplate').ApplyTemplateOptions
}

/**
 * Materialize a component-job starter scaffold through the shared SDK path.
 *
 * Combines `createComponentJobScaffoldTemplate(jobRootPath, { template })` with
 * `applyWorkspaceTemplate(session, template, applyOptions)` in one call, matching
 * what `starterScaffolds.ts` does internally at lines 77–78.
 *
 * Preserves `job.json` and `.job/*` outside the template artifact (those are
 * server-runner concerns, not starter content), per §71 §4.1.
 */
export async function applyComponentJobScaffold(
    options: ApplyComponentJobScaffoldOptions,
): Promise<import('./workspaceTemplate').ApplyTemplateResult> {
    const { session, jobRootPath, template = 'container-service', applyOptions } = options
    const template_ = createComponentJobScaffoldTemplate(jobRootPath, { template })
    return applyWorkspaceTemplate(session, template_, applyOptions)
}

// ── Guest agent client export (§229) ─────────────────────────────
export { createAgentClient } from './componentJobs/guestAgent'
export type {
    CreateAgentClientOptions,
    GuestAgentClient,
    GuestAgentTranscriptEvent,
} from './componentJobs/guestAgent'
